/**
 * =============================================================================
 * SUMMARY FINDINGS WIDGETS - FIXED VERSION
 * =============================================================================
 *
 * CRITICAL FIXES APPLIED:
 * 1. Proper data extraction from BOTH localhost and network_devices
 * 2. Improved column styling with fixed widths
 * 3. Filtered "Unknown" OS from OS widgets
 * 4. Handle missing backup data gracefully
 * 5. Separate widgets for Network vs Localhost scans
 *
 * Data Sources:
 * - localhost."Hardware Informations".* (primary for localhost scan)
 * - network_devices[].system.* (primary for network scan)
 */

import React, { useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { getLabelStyle } from './widgetLabelHelpers';
import { extractADAdminsData } from './ADAdminsTableWidget';
import './SummaryFindingsWidgets.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_PIE_COLORS = {
  risk: '#dc3545',      // Red for issues/risks
  healthy: '#28a745',   // Green for healthy/compliant
  warning: '#ffc107',   // Yellow for warnings
  info: '#17a2b8'       // Blue for info
};

const DEFAULT_PIE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        boxWidth: 12,
        padding: 10,
        font: { size: 11 }
      }
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const label = context.label || '';
          const value = context.parsed || 0;
          const total = context.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          return `${label}: ${value} (${percentage}%)`;
        }
      }
    }
  }
};

// Unit costs for findings (matching backend template)
const UNIT_COSTS = {
  'Backups': 50,
  'Admins - Non-Expiring Password': 25,
  'Admins - Daily Work': 25,
  'Accounts - Non-Expiring Password': 10,
  'Firewall': 30,
  'OS - Unsupported': 100,
  'Services - Non-Default': 15,
  'Open Ports - Sensitive': 40,
  'Disk Usage': 20,
  'Device Age': 75,
  'PII': 100,
  // Backend unitCost values from template2/Common.js
  'Accounts': 50,
  'Admin Accounts': 100,
  'Drive': 50,
  'Age': 100,
  'Softwares': 50,
  'Antivirus': 100, // Using the backend value (100) instead of 60
  'Encryption': 50,
  'OS': 50,
  'Services': 50,
  'Open Ports': 30,
  'Dark Web': 100,
  'AD Computers': 50,
  'AD Users': 50,
  'MS Users': 50,
  'MFA': 50,
  'AD Groups': 40,
  'FSMO': 50,
  'GPO': 20,
  'OU': 40,
  'Passwords': 30,
  'Policy Configuration': 100,
  'Localhost Firewall': 50,
  'Localhost Services': 50,
  'Localhost OS': 50,
  'Localhost Open Ports': 30
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

/**
 * Simple table component for summary statistics
 */
const SummaryTable = ({
  title,
  headers,
  rows,
  className = '',
  config,
  widgetType,
  data = null
}) => {
  if (rows.length === 0) {
    return null;
  }

  // Column Visibility Filtering
  let displayedHeaders = headers;
  let displayedRows = rows;

  if (config?.includedColumns && Array.isArray(config.includedColumns) && config.includedColumns.length > 0) {
    const indices = [];
    headers.forEach((h, i) => {
      if (config.includedColumns.includes(h)) {
        indices.push(i);
      }
    });

    if (indices.length > 0) {
      displayedHeaders = indices.map(i => headers[i]);
      displayedRows = rows.map(row => indices.map(i => row[i]));
    }
  }

  // Get label styles - Pass data for dynamic status colors
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType, data);
  const headerStyle = getLabelStyle(config, 'headers', {
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    textAlign: 'left'
  }, widgetType, data);
  const rowStyle = getLabelStyle(config, 'rows', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#212529',
    textAlign: 'left'
  }, widgetType, data);


  return (
    <div className={`findings-summary-table ${className}`}>
      {title && <div className="summary-table-title" style={titleStyle}>{title}</div>}
      <table>
        <thead>
          <tr>
            {displayedHeaders.map((header, idx) => (
              <th key={idx} style={headerStyle}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedRows.map((row, idx) => (
            <tr key={idx}>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx} style={rowStyle}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Pie chart wrapper component with Styling Support
 */
const PieChartWidget = ({ title, chartData, height = 200, config = {}, widgetType = null, data = null }) => {
  // Get title style
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'left'
  }, widgetType, data);


  // Merge options with styling
  const options = {
    ...DEFAULT_PIE_OPTIONS,
    plugins: {
      ...DEFAULT_PIE_OPTIONS.plugins,
      legend: {
        ...DEFAULT_PIE_OPTIONS.plugins.legend,
        display: config.legendPosition !== 'none',
        position: config.legendPosition || DEFAULT_PIE_OPTIONS.plugins.legend.position,
        labels: {
          ...DEFAULT_PIE_OPTIONS.plugins.legend.labels,
          // Apply font/color to legend if needed, for now keep default
        }
      },
      title: {
        display: false // We render custom title div
      }
    }
  };

  return (
    <div className="findings-pie-chart-widget">
      {title && <div className="chart-title" style={titleStyle}>{title}</div>}
      <div style={{ height: `${height}px`, position: 'relative' }}>
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
};

// ============================================================================
// DATA EXTRACTION HELPERS
// ============================================================================

/**
 * Extract admins data from BOTH localhost and network_devices
 */
export const extractAdminsData = (data) => {
  let allAdmins = [];
  let regularAdmins = [];

  // Try localhost first (preferred source for localhost scans)
  const localhostAdmins = data?.localhost?.["Hardware Informations"]?.Administrators;
  if (localhostAdmins && typeof localhostAdmins !== 'string') {
    // Localhost admins format may be different
    // For now, skip if it's an error string
  }

  const localhostRegularAdmins = data?.localhost?.["Hardware Informations"]?.regular_admins;
  if (Array.isArray(localhostRegularAdmins)) {
    regularAdmins = [...regularAdmins, ...localhostRegularAdmins];
  }

  // Try network_devices
  const networkDevices = data?.network_devices || [];
  networkDevices.forEach(device => {
    if (!device.system || device.system === "WMI Error") return;

    const admins = device.system.Admins || [];
    if (Array.isArray(admins)) {
      allAdmins = [...allAdmins, ...admins];
    }

    const regAdmins = device.system.regular_admins || [];
    if (Array.isArray(regAdmins)) {
      regularAdmins = [...regularAdmins, ...regAdmins];
    }
  });

  return { allAdmins, regularAdmins };
};

/**
 * Extract user accounts from BOTH localhost and network_devices
 */
const extractAccountsData = (data) => {
  let allUsers = [];

  // Try localhost first
  const localhostUsers = data?.localhost?.["Hardware Informations"]?.["Local Users"];
  if (Array.isArray(localhostUsers)) {
    allUsers = [...allUsers, ...localhostUsers];
  }

  // Try network_devices
  const networkDevices = data?.network_devices || [];
  networkDevices.forEach(device => {
    if (!device.system || device.system === "WMI Error") return;

    const users = device.system["Local Users"] || [];
    if (Array.isArray(users)) {
      allUsers = [...allUsers, ...users];
    }
  });

  return allUsers;
};

/**
 * Extract firewall data from BOTH localhost and network_devices
 */
const extractFirewallData = (data) => {
  let firewallDevices = [];

  // Try localhost first
  const localhostFirewall = data?.localhost?.["Hardware Informations"]?.Firewall;
  if (localhostFirewall && typeof localhostFirewall === 'string' && !localhostFirewall.includes("Error")) {
    firewallDevices.push({ source: 'localhost', status: localhostFirewall });
  }

  // Try network_devices
  const networkDevices = data?.network_devices || [];
  networkDevices.forEach(device => {
    if (!device.system || device.system === "WMI Error") return;

    const firewall = device.system.Firewall;
    if (firewall) {
      firewallDevices.push({ source: device.ip || 'network', status: firewall });
    }
  });

  return firewallDevices;
};

/**
 * Extract OS data from BOTH localhost and network_devices
 * FILTERS OUT "Unknown" OS!
 */


// ============================================================================
// BACKUPS WIDGETS
// ============================================================================

/**
 * Backups Summary Widget (backward-compatible wrapper)
 * Now renders the combined BackupsCostSummaryWidget
 */
export const BackupsSummaryWidget = ({ data, title, config, ...props }) => {
  return <BackupsCostSummaryWidget data={data} title={title || config?.title || "Backups Cost Summary"} config={config} {...props} />;
};

/**
 * Backups Cost Summary Widget (COMBINED with BackupsSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const BackupsCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    const networkDevices = data?.network_devices ||
      data?.scanData?.network_devices ||
      data?.data?.network_devices ||
      [];

    let nobackup = 0;
    let total = networkDevices.length;
    let hasBackupData = false;

    try {
      for (const device of networkDevices) {
        const backups = device?.system?.['backups'];
        if (!backups) continue;
        hasBackupData = true;
        const notes = backups['notes'] || backups['Notes'] || '';
        if (!notes.includes("Backup completed.")) {
          nobackup++;
        }
      }
    } catch (error) {
      console.log("Error BackupsCostSummaryWidget", error);
    }

    const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Backups'] || 50);
    const itemCost = Math.ceil(unitCost * nobackup);
    const riskPercent = total > 0 ? `${((100 * nobackup / total).toFixed(2))}%` : '0%';

    return {
      item: 'Backup',
      totalCount: total,
      count: nobackup,
      unitCost,
      itemCost,
      riskPercent,
      hasBackupData
    };
  }, [data, customUnitCost]);

  if (!costData.hasBackupData) {
    return null;
  }

  return (
    <SummaryTable
      title={title || config?.title || "Backup cost summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="backups-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// ENCRYPTION WIDGETS
// ============================================================================

/**
 * Encryption Summary Widget (backward-compatible wrapper)
 * Now renders the combined EncryptionCostSummaryWidget
 */
export const EncryptionSummaryWidget = ({ data, title, config, ...props }) => {
  return <EncryptionCostSummaryWidget data={data} title={title || config?.title || "Encryption Cost Summary"} config={config} {...props} />;
};

/**
 * Encryption Cost Summary Widget
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const EncryptionCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const networkDevices = scanDataSource?.network_devices || [];
    const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || null;

    let unencryptedCount = 0;
    let totalScanned = 0;
    let hasEncryptionData = false;

    // Helper to check encryption status consistently
    const checkIsEncrypted = (encryption) => {
      if (encryption === undefined || encryption === null) return null;

      if (typeof encryption === 'string') {
        const encLower = encryption.toLowerCase();
        // If it doesn't contain "disabled", "off", or "not enabled", we assume it's encrypted or healthy
        if (!encLower.includes('disabled') && !encLower.includes('off') && !encLower.includes('not enabled')) {
          return true;
        }
        return false;
      } else if (Array.isArray(encryption)) {
        // Standard array-of-arrays format: [['C:', 'Status (Encrypted)'], ...]
        if (encryption.length === 0) return false;
        for (const enc of encryption) {
          const status = (Array.isArray(enc) ? enc[1] : enc) || '';
          if (status.toLowerCase().includes('(encrypted)')) {
            return true;
          }
        }
        return false;
      } else if (typeof encryption === 'object') {
        // Object format: { enabled: true, type: "BitLocker" }
        if (encryption.enabled === true || encryption.status === 'Enabled' || encryption.Enabled === true) {
          return true;
        }
        return false;
      }
      return false;
    };

    const processDevice = (device) => {
      if (!device || !device.system || device.system === "WMI Error") return;
      
      const encryption = device.system.Encryption || device.system.encryption;
      if (encryption === undefined) return;

      hasEncryptionData = true;
      totalScanned++;
      
      const isEncrypted = checkIsEncrypted(encryption);
      if (isEncrypted === false) {
        unencryptedCount++;
      }
    };

    // 1. Process Network Devices
    networkDevices.forEach(processDevice);

    // 2. Process Localhost (if not already included in networkDevices)
    if (localhost) {
      // Localhost data structure varies, check common locations for encryption
      const encryption = localhost?.System?.Encryption || 
                         localhost?.["Hardware Informations"]?.Encryption || 
                         localhost?.Encryption || 
                         localhost?.encryption ||
                         localhost?.System?.encryption ||
                         localhost?.["Hardware Informations"]?.encryption;
      
      if (encryption !== undefined) {
        hasEncryptionData = true;
        totalScanned++;
        const isEncrypted = checkIsEncrypted(encryption);
        if (isEncrypted === false) {
          unencryptedCount++;
        }
      } else if (localhost["Hardware Informations"]?.disks) {
        // Fallback: check disks if encryption is not a top-level property
        // This is less common but possible in some scan versions
        hasEncryptionData = true;
        totalScanned++;
        // If we have disks but no explicit encryption info, we might not be able to determine status
        // so we don't increment unencryptedCount unless we are sure.
        // For now, if we found disks, we count it as scanned.
      }
    }

    const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Encryption'] || 50);
    const itemCost = Math.ceil(unitCost * unencryptedCount);
    const riskPercent = totalScanned > 0 ? `${((100 * unencryptedCount / totalScanned).toFixed(2))}%` : '0%';

    return {
      item: 'Encryption',
      totalCount: totalScanned,
      count: unencryptedCount,
      unitCost,
      itemCost,
      riskPercent,
      hasEncryptionData
    };
  }, [data, customUnitCost]);

  if (!costData.hasEncryptionData) {
    return null;
  }

  return (
    <SummaryTable
      title={title || config?.title || "Encryption cost summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="network-encryption-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// ADMINS WIDGETS
// ============================================================================

/**
 * Admins Password Status Pie Chart
 * Matches _Network_Account_status logic from template2/Network.js
 * Processes all Local Users and counts expiring vs non-expiring passwords
 */
export const AdminsPasswordStatusPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    let expire = 0;
    let noexpire = 0;

    try {
      // Extract ONLY network_devices - match backend _Network_Admin_Account_status logic
      // Backend function: _Network_Admin_Account_status processes device?.system?.['Administrators']
      const networkDevices = data?.network_devices || [];

      // Process Administrators from network devices (matches backend _Network_Admin_Account_status)
      // Backend logic: if (user['Password Expires']) expire++; else noexpire++;
      networkDevices.forEach(device => {
        // Skip if system is missing or error
        if (!device?.system || device.system === "WMI Error") return;

        // Match backend: const users = device?.system?.['Administrators'];
        const users = device?.system?.['Administrators'];
        if (!Array.isArray(users)) return;

        users.forEach(user => {
          // Match backend exactly: if (user['Password Expires']) expire++; else noexpire++;
          // Backend checks truthiness, so handle both 'Password Expires' and 'PasswordExpires' field names
          const passwordExpires = user['Password Expires'] !== undefined
            ? user['Password Expires']
            : user['PasswordExpires'];

          // Match backend logic: truthy = expires, falsy = doesn't expire
          if (passwordExpires) {
            expire++;
          } else {
            noexpire++;
          }
        });
      });
    } catch (error) {
      console.log('Error AdminsPasswordStatusPieChartWidget', error);
    }

    // Match backend pie chart structure from Network_Admin_Account_status
    // Backend uses: [values[1], values[0]] which is [noexpire, expire]
    // Labels: ["Non-expiring passwords", "Expiring passwords"]
    // Include values in labels for better readability
    return {
      labels: [
        `Non-expiring passwords (${noexpire})`,
        `Expiring passwords (${expire})`
      ],
      datasets: [{
        data: [noexpire, expire],
        backgroundColor: [DEFAULT_PIE_COLORS.risk, DEFAULT_PIE_COLORS.healthy],
        borderWidth: 1
      }]
    };
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "Admins Password Status"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="admins-password-status-pie"
      data={data}
    />
  );
};

/**
 * Admins Finding Summary (backward-compatible wrapper)
 * Now renders the combined AdminsCostSummaryWidget
 */
export const AdminsFindingSummaryWidget = ({ data, title, config, ...props }) => {
  return <AdminsCostSummaryWidget data={data} title={title || config?.title || "Admins Cost Summary"} config={config} {...props} />;
};

/**
 * Admins Cost Summary (COMBINED with AdminsFindingSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const AdminsCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    let noexpire = 0;
    let total = 0;
    let regularAdminsCount = 0;

    try {
      const networkDevices = data?.network_devices || [];

      networkDevices.forEach(device => {
        if (!device?.system || device.system === "WMI Error") return;

        const users = device?.system?.['Administrators'];
        if (Array.isArray(users)) {
          users.forEach(user => {
            const passwordExpires = user['Password Expires'] !== undefined
              ? user['Password Expires']
              : user['PasswordExpires'];

            if (!passwordExpires) {
              noexpire++;
            }
            total++;
          });
        }

        const regularAdmins = device?.system?.['regular_admins'];
        if (Array.isArray(regularAdmins)) {
          regularAdminsCount += regularAdmins.length;
        }
      });

      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Admin Accounts'] || 100);
      const issueCount = noexpire + regularAdminsCount;
      const itemCost = Math.ceil(unitCost * issueCount);
      const riskPercent = total > 0 ? `${Math.round(100 * noexpire / total)}%` : '0%';

      return {
        item: 'Admins',
        totalCount: total,
        count: issueCount,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error AdminsCostSummaryWidget', error);
      return {
        item: 'Admins',
        totalCount: 0,
        count: 0,
        unitCost: customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Admin Accounts'] || 100),
        itemCost: 0,
        riskPercent: '0%'
      };
    }
  }, [data, customUnitCost]);

  return (
    <SummaryTable
      title={title || config?.title || "Admins Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="admins-cost-summary"
      data={data}
    />
  );
};

/**
 * AD Privileged Admins Cost Summary
 * Shows: Item, Total Count (Global), Count (Privileged), Unit Cost, Total Item Cost, Risk %
 */
export const ADAdminsCostSummaryWidget = ({ data, customUnitCost = null, title, config }) => {
  const costData = useMemo(() => {
    const allAdmins = extractADAdminsData(data);
    const enabledAdmins = allAdmins.filter(a => a.isEnabled);
    const staleAdmins = allAdmins.filter(a => a.isStale);
    
    // In AD contexts, "Total" is often the count of all user objects if we want a denominator
    // But for this specific widget, we'll use the total count of privileged accounts found
    const totalCount = allAdmins.length;
    const issueCount = enabledAdmins.length; // Focus on enabled privileged accounts
    
    const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Admin Accounts'] || 100);
    const itemCost = Math.ceil(unitCost * issueCount);
    const riskPercent = totalCount > 0 ? `${Math.round((issueCount / totalCount) * 100)}%` : '0%';

    return {
      item: 'AD Privileged Admins',
      totalCount,
      count: issueCount,
      staleCount: staleAdmins.length,
      unitCost,
      itemCost,
      riskPercent
    };
  }, [data, customUnitCost]);

  return (
    <SummaryTable
      title={title || config?.title || "AD Admins Cost Summary"}
      headers={['Item', 'Total Privileged', 'Enabled', 'Unit Cost', 'Total Item Cost', 'Status']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.count > 0 ? 'Review Required' : 'Healthy'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="ad-admins-cost-summary"
      data={data}
    />
  );
};

/**
 * AD Admins Status Pie Chart
 * Displays Enabled vs Disabled vs Stale accounts
 */
export const ADAdminsStatusPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    const allAdmins = extractADAdminsData(data);
    
    let enabled = 0;
    let stale = 0;
    let disabled = 0;

    allAdmins.forEach(admin => {
      if (!admin.isEnabled) {
        disabled++;
      } else if (admin.isStale) {
        stale++;
      } else {
        enabled++;
      }
    });

    return {
      labels: [
        `Active Enabled (${enabled})`,
        `Stale Enabled (${stale})`,
        `Disabled (${disabled})`
      ],
      datasets: [{
        data: [enabled, stale, disabled],
        backgroundColor: [
          DEFAULT_PIE_COLORS.healthy, 
          DEFAULT_PIE_COLORS.warning, 
          DEFAULT_PIE_COLORS.info
        ],
        borderWidth: 1
      }]
    };
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "AD Admin Distribution"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="ad-admins-status-pie"
      data={data}
    />
  );
};

// ============================================================================
// ACCOUNTS WIDGETS
// ============================================================================

/**
 * Accounts Password Status Pie Chart
 */
export const AccountsPasswordStatusPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    const allUsers = extractAccountsData(data);

    let expiring = 0;
    let nonExpiring = 0;

    allUsers.forEach(user => {
      if (user['Password Expires']) {
        expiring++;
      } else {
        nonExpiring++;
      }
    });

    // Include values in labels for better readability
    return {
      labels: [
        `Non-Expiring Password (${nonExpiring})`,
        `Expiring Password (${expiring})`
      ],
      datasets: [{
        data: [nonExpiring, expiring],
        backgroundColor: [DEFAULT_PIE_COLORS.risk, DEFAULT_PIE_COLORS.healthy],
        borderWidth: 1
      }]
    };
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "Accounts Password Status"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="accounts-password-pie-chart"
      data={data}
    />
  );
};

/**
 * Accounts Finding Summary (backward-compatible wrapper)
 * Now renders the combined AccountsCostSummaryWidget
 */
export const AccountsFindingSummaryWidget = ({ data, title, config, ...props }) => {
  return <AccountsCostSummaryWidget data={data} title={title || config?.title || "Accounts Cost Summary"} config={config} {...props} />;
};

/**
 * Accounts Cost Summary (COMBINED with AccountsFindingSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const AccountsCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    let noexpire = 0;
    let total = 0;
    let regularAdminsCount = 0;

    try {
      const networkDevices = data?.network_devices || [];

      networkDevices.forEach(device => {
        if (!device?.system || device.system === "WMI Error") return;

        const users = device?.system?.['Local Users'];
        if (Array.isArray(users)) {
          users.forEach(user => {
            const passwordExpires = user['Password Expires'] !== undefined
              ? user['Password Expires']
              : user['PasswordExpires'];

            if (!passwordExpires) {
              noexpire++;
            }
            total++;
          });
        }

        const regularAdmins = device?.system?.['regular_admins'];
        if (Array.isArray(regularAdmins)) {
          regularAdminsCount += regularAdmins.length;
        }
      });

      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Accounts'] || 50);
      const issueCount = noexpire + regularAdminsCount;
      const itemCost = Math.ceil(unitCost * issueCount);
      const riskPercent = total > 0 ? `${Math.round(100 * noexpire / total)}%` : '0%';

      return {
        item: 'Accounts',
        totalCount: total,
        count: issueCount,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error AccountsCostSummaryWidget', error);
      return {
        item: 'Accounts',
        totalCount: 0,
        count: 0,
        unitCost: customUnitCost !== null ? customUnitCost : (UNIT_COSTS['Accounts'] || 50),
        itemCost: 0,
        riskPercent: '0%'
      };
    }
  }, [data, customUnitCost]);

  return (
    <SummaryTable
      title={title || config?.title || "Accounts Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="accounts-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// FIREWALL WIDGETS
// ============================================================================

/**
 * Firewall Finding Summary (backward-compatible wrapper)
 * Now renders the combined FirewallCostSummaryWidget
 */
export const FirewallFindingSummaryWidget = ({ data, title, config, ...props }) => {
  return <FirewallCostSummaryWidget data={data} title={title || config?.title || "Firewall Cost Summary"} config={config} {...props} />;
};

/**
 * Localhost Firewall Findings Summary (backward-compatible wrapper)
 * Now renders the combined LocalhostFirewallCostSummaryWidget
 */
export const LocalhostFirewallFindingsSummaryWidget = ({ data, title, config, ...props }) => {
  return <LocalhostFirewallCostSummaryWidget data={data} title={title || config?.title || "Firewall Cost Summary"} config={config} {...props} />;
};

/**
 * Localhost Service Risk Summary (backward-compatible wrapper)
 * Now renders the combined LocalhostServiceCostSummaryWidget
 */
export const LocalhostServiceRiskSummaryWidget = ({ data, title, config, ...props }) => {
  return <LocalhostServiceCostSummaryWidget data={data} title={title || config?.title || "Service Cost Summary"} config={config} {...props} />;
};

/**
 * Localhost OS Cost Summary Widget
 * Displays cost breakdown for Localhost OS remediation
 * Based on template2/Localhost.js:1006-1018 (OS Cost Summary table)
 */
export const LocalhostOSCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend _Localhost_OS_status logic
      const scanDataSource = data?.scanData || data?.data || data;
      const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};

      let critical = 0;
      let moderate = 0;

      // Backend lines 946-948: const osName = localhostData?.["Hardware Informations"]?.["OS"] || ""; const osVersion = localhostData?.["Hardware Informations"]?.["OS Version"] || ""; const fullOS = `${osName} ${osVersion}`;
      const hardware = localhost?.["Hardware Informations"] || {};
      const osName = hardware?.["OS"] || hardware?.OS || "";
      const osVersion = hardware?.["OS Version"] || hardware?.["OS_Version"] || hardware?.OSVersion || "";
      const fullOS = `${osName} ${osVersion}`.trim();

      if (fullOS) {
        // Backend lines 952-963: Determine OS_Risk and increment critical/moderate
        // Backend: if (/Windows XP|Vista|7|8(?!\.1)|2003|2008|2012/i.test(fullOS)) { OS_Risk = 'Critical'; critical++; }
        if (/Windows XP|Vista|7|8(?!\.1)|2003|2008|2012/i.test(fullOS)) {
          critical++;
        }
        // Backend: else if (/Windows 10|2016|2019/i.test(fullOS)) { OS_Risk = 'Moderate'; moderate++; }
        else if (/Windows 10|2016|2019/i.test(fullOS)) {
          moderate++;
        }
        // Backend: else if (/Windows 11|2022/i.test(fullOS)) { OS_Risk = 'Normal'; } - no increment
      }

      // Backend lines 987-989: const unitcost = unitCost('OS'); const item_cost = unitcost * (values[1] + values[2]); cost += item_cost;
      // Backend unitCost('OS') = 50 (from template2/Common.js:523)
      const unitCost = 50; // Backend unitCost('OS') = 50
      const itemCost = unitCost * (critical + moderate); // Backend: item_cost = unitcost * (critical + moderate)
      const totalCost = itemCost; // In backend, cost is accumulated, but for widget we show item_cost as total_cost

      return {
        item: 'OS',
        unitCost: unitCost,
        itemCost: itemCost,
        totalCost: totalCost
      };
    } catch (error) {
      console.log('Error LocalhostOSCostSummaryWidget', error);
      return {
        item: 'OS',
        unitCost: 50,
        itemCost: 0,
        totalCost: 0
      };
    }
  }, [data]);

  // Backend headers: ['Item', "Unit Cost", "Item Cost", "Total Cost"]
  // Backend rows: [["OS", unitcost, item_cost, cost]]
  return (
    <SummaryTable
      title={title || config?.title || "OS Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        1, // Total Count (Localhost = 1 device)
        (costData.itemCost > 0 ? 1 : 0), // Count
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        (costData.itemCost > 0 ? '100%' : '0%') // Risk %
      ]]}
      className="cost-summary"
      config={config}
      widgetType="localhost-os-cost-summary"
      data={data}
    />
  );
};

/**
 * Localhost Open Ports Cost Summary Widget
 * Displays cost breakdown for Localhost Open Ports remediation
 * Based on template2/Localhost.js:1335-1347 (Open Ports Cost Summary table)
 */
export const LocalhostOpenPortsCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend _Localhost_openPort_status logic
      const scanDataSource = data?.scanData || data?.data || data;
      const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};

      const rows = [];

      // Backend lines 1257-1261: Define sensitive ports
      const sensitive_ports = {
        21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP",
        53: "DNS", 80: "HTTP", 139: "NetBIOS", 143: "SMB",
        3306: "MySQL", 3389: "RDP", 8080: "HTTP"
      };

      // Backend line 1264: const openPorts = localhostData?.["External vulnerability"]?.["open_ports"] || {};
      const openPorts = localhost?.["External vulnerability"]?.["open_ports"] ||
        localhost?.["External Vulnerability"]?.["open_ports"] ||
        localhost?.["external_vulnerability"]?.["open_ports"] ||
        {};

      if (!openPorts || typeof openPorts !== 'object') {
        return {
          item: 'Open Ports',
          unitCost: 30,
          itemCost: 0,
          totalCost: 0
        };
      }

      // Backend lines 1265-1276: Count sensitive ports
      const ipList = Object.keys(openPorts);

      for (const ip of ipList) {
        const ports = openPorts[ip];
        if (!ports || typeof ports !== 'object') continue;

        for (const portKey of Object.keys(ports)) {
          const portNum = parseInt(portKey);

          // Backend lines 1271-1273: if (sensetive_ports[portNum]) { rows.push([ip, portNum, sensetive_ports[portNum]]); }
          if (sensitive_ports[portNum]) {
            rows.push([ip, portNum, sensitive_ports[portNum]]);
          }
        }
      }

      // Backend line 1281: const hasSensitive = rows.length > 0 ? 1 : 0;
      // Backend line 1302: const item_cost = unitcost * values[3];
      // values[3] is hasSensitive, which is 1 if there are any sensitive ports, 0 otherwise
      // This means cost is either 0 (no sensitive ports) or unitcost (has sensitive ports)
      // It's a fixed cost per device if it has sensitive ports, not per port
      const hasSensitive = rows.length > 0 ? 1 : 0; // Backend: binary flag (1 if any sensitive ports, 0 otherwise)

      // Backend lines 1301-1303: const unitcost = unitCost('Open Ports'); const item_cost = unitcost * values[3]; cost += item_cost;
      // Backend unitCost('Open Ports') = 30 (from template2/Common.js:525)
      const unitCost = 30; // Backend unitCost('Open Ports') = 30
      const itemCost = unitCost * hasSensitive; // Backend: item_cost = unitcost * hasSensitive (count of sensitive ports)
      const totalCost = itemCost; // In backend, cost is accumulated, but for widget we show item_cost as total_cost

      return {
        item: 'Open Ports',
        unitCost: unitCost,
        itemCost: itemCost,
        totalCost: totalCost
      };
    } catch (error) {
      console.log('Error LocalhostOpenPortsCostSummaryWidget', error);
      return {
        item: 'Open Ports',
        unitCost: 30,
        itemCost: 0,
        totalCost: 0
      };
    }
  }, [data]);

  // Backend headers: ['Item', 'Unit Cost', 'Item Cost', 'Total Cost']
  // Backend rows: [["Open Ports", unitcost, item_cost, cost]]
  return (
    <SummaryTable
      title={title || config?.title || "Open Ports Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        1, // Total Count (Localhost = 1 device)
        (costData.itemCost > 0 ? 1 : 0), // Count
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        (costData.itemCost > 0 ? '100%' : '0%') // Risk %
      ]]}
      className="cost-summary"
      config={config}
      widgetType="localhost-open-ports-cost-summary"
      data={data}
    />
  );
};

/**
 * Localhost Service Cost Summary (COMBINED with LocalhostServiceRiskSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const LocalhostServiceCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      const scanDataSource = data?.scanData || data?.data || data;
      const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};

      const suspicious = [];
      const ip = 'localhost';
      const services = localhost?.['Hardware Informations']?.['Non-Default Services'];
      const totalDevices = 1;

      if (!Array.isArray(services)) {
        return { item: 'Services', totalCount: totalDevices, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
      }

      for (const service of services) {
        const account = service['Start Account'];
        const state = service['State'];
        const startMode = service['Start Mode'];
        if (["localSystem", "Administrator"].includes(account)) {
          if (state === 'Running' && startMode === 'Auto') {
            suspicious.push([ip, service['Name'] || '', service['Display Name']]);
          }
        }
      }

      const unitCost = 50;
      const itemCost = unitCost * suspicious.length;
      const riskPercent = suspicious.length > 0 ? '100%' : '0%';

      return {
        item: 'Services',
        totalCount: totalDevices,
        count: suspicious.length,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error LocalhostServiceCostSummaryWidget', error);
      return { item: 'Services', totalCount: 1, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
    }
  }, [data]);

  return (
    <SummaryTable
      title={title || config?.title || "Service Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="localhost-service-cost-summary"
      data={data}
    />
  );
};

/**
 * Localhost Firewall Cost Summary (COMBINED with LocalhostFirewallFindingsSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const LocalhostFirewallCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      const scanDataSource = data?.scanData || data?.data || data;
      const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};

      let notFullyEnabledCount = 0;
      const firewall = localhost?.["System"]?.Firewall || localhost?.Firewall;
      const totalProfiles = 1;

      if (!firewall || typeof firewall !== 'object') {
        notFullyEnabledCount++;
      } else {
        const domain = firewall.Domain === true;
        const privateNet = firewall.Private === true;
        const publicNet = firewall.Public === true;
        const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;
        if (enabledCount < 3) notFullyEnabledCount++;
      }

      const unitCost = 50;
      const itemCost = unitCost * notFullyEnabledCount;
      const riskPercent = `${Math.round(100 * notFullyEnabledCount / totalProfiles)}%`;

      return {
        item: 'Firewall',
        totalCount: totalProfiles,
        count: notFullyEnabledCount,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error LocalhostFirewallCostSummaryWidget', error);
      return { item: 'Firewall', totalCount: 0, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
    }
  }, [data]);

  return (
    <SummaryTable
      title={title || config?.title || "Firewall Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="localhost-firewall-cost-summary"
      data={data}
    />
  );
};

/**
 * Firewall Cost Summary (COMBINED with FirewallFindingSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const FirewallCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    const firewallDevices = extractFirewallData(data);
    const totalDevices = firewallDevices.length;
    let disabledCount = 0;

    firewallDevices.forEach(device => {
      const status = device.status.toLowerCase();
      if (status.includes('disabled') || status.includes('off') || status.includes('not running')) {
        disabledCount++;
      }
    });

    const unitCost = UNIT_COSTS['Firewall'];
    const itemCost = Math.ceil(unitCost * disabledCount);
    const riskPercent = totalDevices > 0 ? `${Math.round(100 * disabledCount / totalDevices)}%` : '0%';

    return {
      item: 'Firewall Disabled',
      totalCount: totalDevices,
      count: disabledCount,
      unitCost,
      itemCost,
      riskPercent
    };
  }, [data]);

  if (costData.count === 0) {
    return null;
  }

  return (
    <SummaryTable
      title={title || config?.title || "Firewall Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="firewall-cost-summary"
      data={data}
    />
  );
};

/**
 * Network Firewall Findings Summary (backward-compatible wrapper)
 * Now renders the combined NetworkFirewallCostSummaryWidget
 */
export const NetworkFirewallFindingsSummaryWidget = ({ data, title, config, ...props }) => {
  return <NetworkFirewallCostSummaryWidget data={data} title={title || config?.title || "Firewall Cost Summary"} config={config} {...props} />;
};

/**
 * Network Firewall Cost Summary
 * Matches Network_Firewall_status cost summary from template2/Network.js:1849-1861
/**
 * Network Firewall Cost Summary (COMBINED with NetworkFirewallFindingsSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const NetworkFirewallCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const networkDevices = scanDataSource?.network_devices || [];

    const validDevices = networkDevices.filter(device =>
      device?.system && device.system !== "WMI Error"
    );

    let notFullyEnabledCount = 0;
    const totalDevices = validDevices.length || 0;

    try {
      for (const device of validDevices) {
        const firewall = device?.system?.Firewall;
        if (!firewall || typeof firewall !== 'object') {
          notFullyEnabledCount++;
          continue;
        }
        const enabledCount = [firewall.Domain === true, firewall.Private === true, firewall.Public === true].filter(Boolean).length;
        if (enabledCount < 3) notFullyEnabledCount++;
      }
    } catch (error) {
      console.log('Error NetworkFirewallCostSummaryWidget', error);
    }

    const unitCost = 50;
    const itemCost = unitCost * notFullyEnabledCount;
    const riskPercent = totalDevices > 0 ? `${Math.round(100 * notFullyEnabledCount / totalDevices)}%` : '0%';

    return {
      item: 'Firewall',
      totalCount: totalDevices,
      count: notFullyEnabledCount,
      unitCost,
      itemCost,
      riskPercent
    };
  }, [data]);

  if (costData.count === 0) {
    return null;
  }

  return (
    <SummaryTable
      title={title || config?.title || "Firewall Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="network-firewall-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// OS VERSIONS WIDGETS
// ============================================================================

/**
 * OS Risk Summary (backward-compatible wrapper)
 * Now renders the combined OSCostSummaryWidget
 */
export const OSRiskSummaryWidget = ({ data, title, config, ...props }) => {
  return <OSCostSummaryWidget data={data} title={title || config?.title || "OS Cost Summary"} config={config} {...props} />;
};

/**
 * OS Cost Summary (COMBINED with OSRiskSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const OSCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const networkDevices = scanDataSource?.network_devices || [];

    let critical = 0;
    let moderate = 0;
    let total = 0;

    try {
      for (const device of networkDevices) {
        const OS = device?.system?.['OS'];
        if (!OS) continue;
        if (/Windows XP|Vista|Windows 7|Windows 8(?!\.1)|Server 2003|Server 2008|Server 2012/.test(OS)) {
          critical++;
        } else if (/Windows 10|Server 2016|Server 2019/.test(OS)) {
          moderate++;
        }
        total++;
      }
    } catch (error) {
      console.log('Error OSCostSummaryWidget', error);
    }

    const unitCost = 50;
    const issueCount = critical + moderate;
    const itemCost = unitCost * issueCount;
    const riskPercent = total > 0 ? `${Math.round(100 * issueCount / total)}%` : '0%';

    return {
      item: 'OS',
      totalCount: total,
      count: issueCount,
      unitCost,
      itemCost,
      riskPercent
    };
  }, [data]);

  if (costData.count === 0) {
    return (
      <div className="no-findings">
        All operating systems are fully supported
      </div>
    );
  }

  return (
    <SummaryTable
      title={title || config?.title || "OS Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="os-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// SERVICES WIDGETS
// ============================================================================

/**
 * Network Service Risk Summary (backward-compatible wrapper)
 * Now renders the combined NetworkServiceCostSummaryWidget
 */
export const NetworkServiceRiskSummaryWidget = ({ data, title, config, ...props }) => {
  return <NetworkServiceCostSummaryWidget data={data} title={title || config?.title || "Service Cost Summary"} config={config} {...props} />;
};

/**
 * Network Service Cost Summary (COMBINED with NetworkServiceRiskSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const NetworkServiceCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    const scanDataSource = data?.scanData || data?.data || data;
    const networkDevices = scanDataSource?.network_devices || [];

    const validDevices = networkDevices.filter(device =>
      device?.system && device.system !== "WMI Error"
    );

    const result = analyzeNetworkServices(validDevices);
    if (!result) {
      return { item: 'Services', totalCount: 0, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
    }

    const [, suspicious, totalDevices, , , affectedDevices] = result;
    const unitCost = 50;
    const itemCost = unitCost * suspicious.length;
    const riskPercent = totalDevices > 0 ? `${Math.round(100 * affectedDevices / totalDevices)}%` : '0%';

    return {
      item: 'Services',
      totalCount: totalDevices,
      count: suspicious.length,
      unitCost,
      itemCost,
      riskPercent
    };
  }, [data]);

  if (costData.count === 0) {
    return (
      <div className="no-findings">
        No high-privilege services found
      </div>
    );
  }

  return (
    <SummaryTable
      title={title || config?.title || "Service Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="network-service-cost-summary"
      data={data}
    />
  );
};

/**
 * AD Computer Cost Summary
 * Matches Ad_Computer_status AD Computer Cost Summary from template2/AD.js:421-433
 * Uses unitCost('AD Computers') = 50 from Common.js
 */
export const ADComputerCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data
      const scanDataSource = data?.scanData || data?.data || data;
      const adData = scanDataSource?.['Ad Scan'] || {};
      const inactives = adData?.['inactive_computers'] || [];

      // Match backend: _Ad_inactive_Computer_status returns [rows, total_inactive, total_users]
      // We need inactive_computers_count = total_inactive
      const inactiveComputersCount = Array.isArray(inactives) ? inactives.length : 0;

      // Match backend: unitCost('AD Computers') = 50
      const unitCost = 50;
      const itemCost = unitCost * inactiveComputersCount;
      // In backend, cost is accumulated, but for this widget we show item_cost as total_cost
      const totalCost = itemCost;

      return {
        item: 'AD Computer',
        unitCost,
        itemCost,
        totalCost,
        count: inactiveComputersCount
      };
    } catch (error) {
      console.log('Error ADComputerCostSummaryWidget', error);
      return {
        item: 'AD Computer',
        unitCost: 50,
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data]);

  if (costData.count === 0) {
    return (
      <div className="no-findings">
        No inactive computers found
      </div>
    );
  }

  return (
    <SummaryTable
      title={title || config?.title || "AD Computer Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.count, // In backend template, Total Count isn't explicitly separated for this
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.count > 0 ? '100%' : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="ad-computer-cost-summary"
      data={data}
    />
  );
};

/**
 * AD Users Cost Summary
 * Matches Ad_Users_status AD Users Cost Summary from template2_doc/AD.js:561-568
 * Uses unitCost('AD Users') = 50 from Common.js
 */
export const ADUsersCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - use EXACT same logic as extractInactiveADUsersData
      // This matches the working ADInactiveUsersTableWidget exactly
      if (!data) {
        return {
          item: 'AD Users',
          unitCost: 50,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Use EXACT same extraction as extractInactiveADUsersData function
      // This is the EXACT code from ProofTablesWidgets.jsx:72-80
      // dataContext structure: { ...effectiveScanData, scanData: effectiveScanData, data: effectiveScanData }
      // Since effectiveScanData is spread at top level, 'Ad Scan' is directly on data
      const scanDataSource = data.scanData || data.data || data;
      const adData = scanDataSource?.['Ad Scan'] || {};
      const inactives = adData?.['inactive_accounts'] || [];

      // Match backend: _Ad_inactive_Account_status returns [rows, total_inactive, total_users]
      // Use same check as extractInactiveADUsersData: if (!Array.isArray(inactives)) return 0
      if (!Array.isArray(inactives)) {
        console.log('[ADUsersCostSummaryWidget] inactive_accounts is not an array. Type:', typeof inactives, 'Value:', inactives);
        console.log('[ADUsersCostSummaryWidget] Data structure check:', {
          hasData: !!data,
          dataKeys: data ? Object.keys(data).slice(0, 15) : [],
          hasAdScanAtTop: !!data?.['Ad Scan'],
          hasScanData: !!data?.scanData,
          scanDataKeys: data?.scanData ? Object.keys(data.scanData).slice(0, 15) : [],
          hasAdData: !!adData,
          adDataKeys: adData ? Object.keys(adData).slice(0, 15) : []
        });
        return {
          item: 'AD Users',
          unitCost: 50,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      const inactiveUsersCount = inactives.length;

      // Debug logging - log the actual extraction to verify it matches table widget
      console.log('[ADUsersCostSummaryWidget] ✅ Extraction successful:', {
        inactiveUsersCount: inactiveUsersCount,
        inactivesLength: inactives.length,
        inactivesIsArray: Array.isArray(inactives),
        firstInactive: inactives.length > 0 ? inactives[0] : null,
        // Verify data paths
        hasScanDataSource: !!scanDataSource,
        hasAdData: !!adData,
        adDataKeys: adData ? Object.keys(adData).slice(0, 10) : [],
        // Calculate expected cost
        unitCost: 50,
        expectedItemCost: 50 * inactiveUsersCount
      });

      // Match backend logic from Ad_Users_status:
      // Only calculate cost if there are users (total_users > 0)
      // But for the widget, we show cost based on inactive_users_count
      // unitcost = unitCost('AD Users') = 50
      // item_cost = unitcost * inactive_users_count
      const unitCost = 50; // Backend unitCost('AD Users') = 50
      const itemCost = unitCost * inactiveUsersCount;
      // In backend, cost is accumulated, but for this widget we show item_cost as total_cost
      const totalCost = itemCost;

      return {
        item: 'AD Users',
        unitCost,
        itemCost,
        totalCost,
        count: inactiveUsersCount
      };
    } catch (error) {
      console.log('Error ADUsersCostSummary Widget', error);
      console.log('ADUsersCostSummaryWidget data structure:', {
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : [],
        hasAdScan: !!(data?.scanData?.['Ad Scan'] || data?.data?.['Ad Scan'] || data?.['Ad Scan']),
        adScanKeys: (data?.scanData?.['Ad Scan'] || data?.data?.['Ad Scan'] || data?.['Ad Scan'] || {}) ? Object.keys(data?.scanData?.['Ad Scan'] || data?.data?.['Ad Scan'] || data?.['Ad Scan'] || {}) : []
      });
      return {
        item: 'AD Users',
        unitCost: 50,
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data]);

  // Always show the table, even if count is 0 (matches backend behavior)
  // Backend shows cost summary table regardless of inactive count
  return (
    <SummaryTable
      title={title || config?.title || "AD Users Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.count,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.count > 0 ? '100%' : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="ad-users-cost-summary"
      data={data}
    />
  );
};

/**
 * OU Cost Summary
 * Matches Ad_Ou_status OU Cost Summary from template2/AD.js:620-632
 * Uses unitCost('OU') = 40 from Common.js
 * Cost calculation: ou_cost = Math.round(ou_unit_cost * count_ou_issue)
 * Where count_ou_issue = number of OUs with High/Medium risk (issues)
 */
export const OUCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - use same logic as extractADOUsRiskData
      if (!data) {
        return {
          item: 'OU',
          unitCost: 40,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Extract from raw scan data
      const scanDataSource = data.scanData || data.data || data;
      const adData = scanDataSource?.['Ad Scan'] || {};
      const ous = adData?.['OU_list'] || [];

      if (!Array.isArray(ous)) {
        return {
          item: 'OU',
          unitCost: 40,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Match backend logic: _Ad_assess_ou_risks returns [rows, issues]
      // issues = rows.filter(issue => issue[3] != 'Low')
      // We need to count OUs with High/Medium risk (not Low)
      let countOU = 0;
      let countOUIssue = 0;

      for (const ou of ous) {
        const name = ou.ou || ou.OU || "Unnamed OU";
        const members = ou.members || ou.Members || [];
        const memberCount = Array.isArray(members) ? members.length : 0;

        let risk = "Low";
        const lowerName = name.toLowerCase();

        // Rule 1: Disabled OU has members
        if (lowerName.includes("disabled") && memberCount > 0) {
          risk = "High";
        }
        // Rule 2: Sensitive name (e.g., admin, backup, service)
        else if (/admin|backup|service/.test(lowerName)) {
          risk = "Medium";
        }
        // Rule 3: Empty OU
        else if (memberCount === 0) {
          risk = "Medium";
        }

        countOU++;
        if (risk !== "Low") {
          countOUIssue++;
        }
      }

      // Match backend: ou_unit_cost = unitCost('OU') = 40
      // ou_cost = Math.round(ou_unit_cost * count_ou_issue)
      const unitCost = 40; // Backend unitCost('OU') = 40
      const itemCost = Math.round(unitCost * countOUIssue);
      // In backend, cost is accumulated, but for this widget we show item_cost as total_cost
      const totalCost = itemCost;

      return {
        item: 'OU',
        unitCost,
        itemCost,
        totalCost,
        count: countOUIssue,
        totalOUs: countOU
      };
    } catch (error) {
      console.log('Error OUCostSummaryWidget', error);
      return {
        item: 'OU',
        unitCost: 40,
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data]);

  // Always show the table, even if count is 0 (matches backend behavior)
  return (
    <SummaryTable
      title={title || config?.title || "OU Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalOUs,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.totalOUs > 0 ? `${Math.round(100 * costData.count / costData.totalOUs)}%` : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="ou-cost-summary"
      data={data}
    />
  );
};

/**
 * AD Groups Cost Summary
 * Matches Ad_Groups_status AD Groups Cost Summary from template2/AD.js:481-493
 * Uses unitCost('AD Groups') = 40 from Common.js
 * Cost calculation: group_cost = Math.round(group_unit_cost * count_group_issue)
 * Where count_group_issue = number of groups with High/Medium risk (issues)
 */
export const ADGroupsCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - use same logic as extractADGroupsRiskData
      if (!data) {
        return {
          item: 'AD Groups',
          unitCost: 40,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Extract from raw scan data
      const scanDataSource = data.scanData || data.data || data;
      const adData = scanDataSource?.['Ad Scan'] || {};
      const groups = adData?.groups || [];

      if (!Array.isArray(groups)) {
        return {
          item: 'AD Groups',
          unitCost: 40,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Match backend logic: _Ad_assess_group_risks returns [rows, issues]
      // issues = rows where risk == 'Medium' || risk == 'High'
      // We need to count groups with High/Medium risk (not Low)
      const privilegedGroups = [
        "Domain Admins",
        "Enterprise Admins",
        "Administrators",
        "Schema Admins",
        "Account Operators",
        "Backup Operators",
        "Remote Desktop Users",
        "Network Configuration Operators",
        "Power Users"
      ];

      let countGroup = 0;
      let countGroupIssue = 0;

      for (const group of groups) {
        const {
          name,
          managedBy,
          members,
          isSecurityGroup,
          isDomainLocal
        } = group;

        // Handle different property name formats
        const groupName = name || group.Name || group.GroupName || '';
        const groupMembers = Array.isArray(members) ? members : (group.Members || []);
        const memberCount = Array.isArray(groupMembers) ? groupMembers.length : 0;
        const groupIsSecurityGroup = isSecurityGroup !== undefined ? isSecurityGroup : (group.IsSecurityGroup || group.GroupType === 2 || group.Type === 'Security');
        const groupIsDomainLocal = isDomainLocal !== undefined ? isDomainLocal : (group.IsDomainLocal || group.GroupScope === 2 || group.Scope === 'Domain Local');
        const groupManagedBy = managedBy || group.ManagedBy || null;

        const isPrivileged = privilegedGroups.includes(groupName);
        const hasManager = !!groupManagedBy;

        let risk = "Low";

        // Rule 1: Privileged group with members
        if (isPrivileged && memberCount > 0) {
          risk = "High";
        }
        // Rule 2: Privileged group with 0 members
        else if (isPrivileged && memberCount === 0) {
          risk = "Medium";
        }
        // Rule 3: No manager and is security group
        else if (!hasManager && groupIsSecurityGroup) {
          risk = "Medium";
        }
        // Rule 4: Domain local and is security group
        else if (groupIsDomainLocal && groupIsSecurityGroup) {
          risk = "Medium";
        }

        countGroup++;
        if (risk !== "Low") {
          countGroupIssue++;
        }
      }

      // Match backend: group_unit_cost = unitCost('AD Groups') = 40
      // group_cost = Math.round(group_unit_cost * count_group_issue)
      const unitCost = 40; // Backend unitCost('AD Groups') = 40
      const itemCost = Math.round(unitCost * countGroupIssue);
      // In backend, cost is accumulated, but for this widget we show item_cost as total_cost
      const totalCost = itemCost;

      return {
        item: 'AD Groups',
        unitCost,
        itemCost,
        totalCost,
        count: countGroupIssue,
        totalGroups: countGroup
      };
    } catch (error) {
      console.log('Error ADGroupsCostSummaryWidget', error);
      return {
        item: 'AD Groups',
        unitCost: 40,
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data]);

  // Always show the table, even if count is 0 (matches backend behavior)
  return (
    <SummaryTable
      title={title || config?.title || "AD Groups Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalGroups,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.totalGroups > 0 ? `${Math.round(100 * costData.count / costData.totalGroups)}%` : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="ad-groups-cost-summary"
      data={data}
    />
  );
};

/**
 * Network Open Ports Cost Summary
 * Matches Network_openPort_status Open Ports Cost Summary from template2/Network.js:1325-1337
 * Uses unitCost('Open Ports') = 30 from Common.js
 */
export const NetworkOpenPortsCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - handle multiple possible structures
      const scanDataSource = data?.scanData || data?.data || data;
      const networkDevices = scanDataSource?.network_devices || [];

      if (!Array.isArray(networkDevices) || networkDevices.length === 0) {
        return {
          item: 'Open Ports',
          unitCost: 30,
          itemCost: 0,
          totalCost: 0,
          count: 0
        };
      }

      // Use the same logic as backend _Network_openPort_status
      const sensitive_ports = {
        21: "FTP",
        22: "SSH",
        23: "Telnet",
        25: "SMTP",
        53: "DNS",
        80: "HTTP",
        139: "NetBIOS",
        143: "SMB",
        3306: "MySQL",
        3389: "RDP",
        8080: "HTTP"
      };

      const rows = [];

      for (const device of networkDevices) {
        const ip = device['ip'] || device['hostname'] || '';
        const open_ports = device['open_ports'] || {};

        // open_ports is an object where keys are port numbers (as strings)
        // Backend: Object.keys(open_ports).map(key => { ... })
        if (open_ports && typeof open_ports === 'object' && !Array.isArray(open_ports)) {
          Object.keys(open_ports).forEach(key => {
            // Check if this port is in the sensitive_ports list
            if (sensitive_ports[key]) {
              rows.push([ip, key, sensitive_ports[key]]);
            }
          });
        }
      }

      // Match backend: sensetive_port_count = new Set(rows.map(entry => entry[0]))
      // This creates a set of unique IP addresses that have sensitive ports
      const sensitive_port_count = new Set(rows.map(entry => entry[0]));
      const affectedDevices = sensitive_port_count.size;

      // Match backend: unitCost('Open Ports') = 30, item_cost = unitcost * values[3]
      // Where values[3] = sensetive_port_count.size (affected devices count)
      const unitCost = 30; // Backend unitCost('Open Ports') = 30
      const itemCost = unitCost * affectedDevices;
      // In backend, cost is accumulated, but for this widget we show item_cost as total_cost
      const totalCost = itemCost;

      return {
        item: 'Open Ports',
        unitCost,
        itemCost,
        totalCost,
        count: affectedDevices
      };
    } catch (err) {
      console.log("Error NetworkOpenPortsCostSummaryWidget", err);
      return {
        item: 'Open Ports',
        unitCost: 30,
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data]);

  if (costData.count === 0) {
    return (
      <div className="no-findings">
        No sensitive open ports found
      </div>
    );
  }

  return (
    <SummaryTable
      title={title || config?.title || "Open Ports Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.count, // In this context affected devices is the count
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.count > 0 ? '100%' : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="network-open-ports-cost-summary"
      data={data}
    />
  );
};

// Helper function for services analysis (needs to be accessible)
function analyzeNetworkServices(devices) {
  const rows = [];
  const suspicious = [];
  let device_suspicious = new Set();
  const all = {};
  const total = devices.length || 0;
  const detail = [];

  try {
    for (const device of devices) {
      const ip = device['ip'] || '';
      const services = device?.system?.['Non-Default Services'];
      if (!Array.isArray(services)) continue;

      for (const service of services) {
        const account = service['Start Account'];
        const state = service['State'];
        const startMode = service['Start Mode'];
        const DN = service['Display Name'];
        const name = service['Name'] || '';
        let risk = 'Low';
        let note = 'Standard non-running service';

        // Count service state
        all[state] = (all[state] || 0) + 1;

        // Only inspect high-privilege accounts
        if (["localSystem", "Administrator"].includes(account)) {
          // Risk assessment
          if (state === 'Running' && startMode === 'Auto') {
            risk = 'High';
            note = 'Running with auto-start and high privileges';
            suspicious.push([
              ip,
              name,
              DN,
              'High privilege service account',
              risk,
              note
            ]);
            device_suspicious.add(ip);
          }
        }

        detail.push([
          ip,
          name,
          DN,
          ["localSystem", "Administrator"].includes(account)
            ? `High privilege service account :${account}`
            : `Running as ${account} account`,
          state,
          startMode
        ]);
      }
    }

    // Build rows with severity tags
    Object.keys(all).forEach(key => {
      let severity = 'Low';
      let explanation = 'Unknown or inactive state — treated as low risk.';
      if (key === 'Running') {
        severity = 'Critical';
        explanation = 'Service is currently running. If combined with high privileges or auto-start, it may present an active risk.';
      } else if (key === 'Stopped') {
        severity = 'Moderate';
        explanation = 'Service is not running but can be started manually. May still pose a risk depending on privilege and access.';
      } else if (key === 'Disabled') {
        severity = 'Low';
        explanation = 'Service is disabled and cannot start unless explicitly re-enabled. Minimal security concern.';
      }
      rows.push([key, all[key], severity, explanation]);
    });
  } catch (err) {
    console.log("Error analyzeNetworkServices", err);
    return null;
  }

  const suspicious_devices = device_suspicious.size;
  const avg = total > 0 ? Math.round(100 * suspicious_devices / total) : 0;

  // Final return: [rows, suspicious, total, detail, avg, suspicious_devices]
  return [rows, suspicious, total, detail, avg, suspicious_devices];
}

// ============================================================================
// PII WIDGETS
// ============================================================================

/**
 * Generate color array for PII pie chart
 * Matches backend generateColorArray function
 */
const generatePIIColorArray = (count) => {
  const colors = [
    '#3b82f6', // Blue
    '#f97316', // Orange
    '#22c55e', // Green
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#f59e0b', // Amber
    '#10b981', // Emerald
    '#f43f5e', // Rose
    '#06b6d4', // Cyan
    '#84cc16', // Lime
    '#ec4899', // Pink
    '#6366f1', // Indigo
    '#14b8a6', // Teal
    '#fbbf24', // Yellow
    '#a855f7'  // Violet
  ];

  // Return colors cycling through the array
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

/**
 * Extract PII type counts - shared function for both pie chart and summary widgets
 * Matches backend getPiiTypeCountRows logic
 * Returns array of [type, count] pairs
 */
const extractPIITypeCounts = (data) => {
  try {
    // Extract from raw scan data - match backend getPiiTypeCountRows logic
    // Backend: function getPiiTypeCountRows(combinedPii = {}) { return Object.entries(combinedPii).map(([type, entries]) => [type, entries.length]); }
    // Backend receives jsonData which is parsed['PII'] - the processed/combined PII data
    // CRITICAL: Backend uses parsed['PII'] which is the result of combinePiiObjects()
    // This is the processed/combined/deduplicated PII data, not raw scan data
    const scanDataSource = data?.scanData || data?.data || data;

    // CRITICAL: Backend uses parsed['PII'] which is the processed/combined PII data
    // Match backend exactly: jsonData is parsed['PII'] passed to PII_detection function
    // Priority: Use 'PII' key first (matches backend parsed['PII'])
    // This should be the processed data from the backend, not raw scan data
    const combinedPii = scanDataSource?.PII ||
      scanDataSource?.['PII'] ||
      scanDataSource?.pii ||
      scanDataSource?.combinedPii ||
      scanDataSource?.['PII Detection'] ||
      {};

    console.log('[extractPIITypeCounts] Data keys:', Object.keys(data || {}).slice(0, 5));
    console.log('[extractPIITypeCounts] ScanDataSource keys:', Object.keys(scanDataSource || {}).slice(0, 10));
    console.log('[extractPIITypeCounts] Combined PII found:', !!combinedPii);
    console.log('[extractPIITypeCounts] Combined PII keys:', Object.keys(combinedPii));

    if (!combinedPii || typeof combinedPii !== 'object' || Object.keys(combinedPii).length === 0) {
      console.log('[extractPIITypeCounts] No PII data - returning empty array');
      return [];
    }

    // Backend: Object.entries(combinedPii).map(([type, entries]) => [type, entries.length])
    // CRITICAL: entries must be an array, count its length
    const piiTypeCounts = Object.entries(combinedPii).map(([type, entries]) => {
      // Backend expects entries to be an array, count its length
      const count = Array.isArray(entries) ? entries.length : 0;
      console.log(`[extractPIITypeCounts] ${type}: count=${count}, isArray=${Array.isArray(entries)}`);
      return [type, count];
    });

    console.log('[extractPIITypeCounts] PII Type Counts (before filter):', piiTypeCounts);

    // Filter out types with 0 count
    const filteredCounts = piiTypeCounts.filter(([type, count]) => count > 0);

    console.log('[extractPIITypeCounts] Filtered Counts (after filter):', filteredCounts);
    console.log('[extractPIITypeCounts] Returning:', filteredCounts.length, 'items');

    return filteredCounts;
  } catch (error) {
    console.log('Error extractPIITypeCounts', error);
    console.error('Error extractPIITypeCounts stack:', error.stack);
    return [];
  }
};

/**
 * PII Detection Pie Chart Widget
 * Displays a pie chart showing PII types and their counts
 * Based on template2/PII.js:39-72 (PII Detection pie chart)
 */
export const PIIDetectionPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    try {
      // Use shared extraction function
      const filteredCounts = extractPIITypeCounts(data);

      // Debug logging
      console.log('[PIIDetectionPieChartWidget] Filtered counts:', filteredCounts);

      if (filteredCounts.length === 0) {
        return {
          labels: ['No PII Detected'],
          datasets: [{
            data: [0],
            backgroundColor: ['#9ca3af'],
            borderWidth: 1
          }]
        };
      }

      // Backend: values.map(value => value[0]) for labels, values.map(value => value[1]) for data
      const labels = filteredCounts.map(([type, count]) => `${type} (${count})`);
      const dataValues = filteredCounts.map(([type, count]) => count);

      // Backend: generateColorArray(values.length) - generate colors for each type
      const colors = generatePIIColorArray(filteredCounts.length);

      // Backend: generatePIIChart("pie", values.map(value => value[0]), values.map(value => value[1]), generateColorArray(values.length), ` `, 'right')
      return {
        labels: labels,
        datasets: [{
          data: dataValues,
          backgroundColor: colors,
          borderWidth: 1
        }]
      };
    } catch (error) {
      console.log('Error PIIDetectionPieChartWidget', error);
      return {
        labels: ['Error loading data'],
        datasets: [{
          data: [0],
          backgroundColor: ['#9ca3af'],
          borderWidth: 1
        }]
      };
    }
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "PII Detection"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="pii-detection-pie-chart"
      data={data}
    />
  );
};

/**
 * PII Finding Summary (backward-compatible wrapper)
 * Now renders the combined PIICostSummaryWidget
 */
export const PIIFindingSummaryWidget = ({ data, title, config, ...props }) => {
  return <PIICostSummaryWidget data={data} title={title || config?.title || "PII Cost Summary"} config={config} {...props} />;
};

/**
 * PII Cost Summary (COMBINED with PIIFindingSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const PIICostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    try {
      const scanDataSource = data?.scanData || data?.data || data;
      const combinedPii = scanDataSource?.PII || scanDataSource?.['PII'] || scanDataSource?.pii || scanDataSource?.combinedPii || scanDataSource?.['PII Detection'] || {};

      // Get PII type counts for total types detected
      const filteredCounts = extractPIITypeCounts(data);
      const totalTypes = filteredCounts.length;

      // Get total PII count
      const total = Object.values(combinedPii).reduce((t, entries) => {
        return t + (Array.isArray(entries) ? entries.length : 0);
      }, 0);

      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['PII'] || 100);
      const itemCost = unitCost * total;
      const riskPercent = totalTypes > 0 ? `${totalTypes} types` : '0%';

      return {
        item: 'PII',
        totalCount: totalTypes,
        count: total,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error PIICostSummaryWidget', error);
      return { item: 'PII', totalCount: 0, count: 0, unitCost: customUnitCost !== null ? customUnitCost : 100, itemCost: 0, riskPercent: '0%' };
    }
  }, [data, customUnitCost]);

  return (
    <SummaryTable
      title={title || config?.title || "PII Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="pii-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// MS365 WIDGETS
// ============================================================================

/**
 * MS Users Pie Chart Widget
 * Displays a pie chart showing Active vs Inactive MS365 users
 * Based on template2/MS.js:113-127 (MS Users pie chart)
 */
export const MSUsersPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend getActiveAndInactiveUserCounts logic
      // Backend: const users = data.users || []; const inactive = data.inactive_accounts || [];
      const scanDataSource = data?.scanData || data?.data || data;
      const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};

      const users = ms365.users || [];
      const inactive = ms365.inactive_accounts || [];

      if (!Array.isArray(users) || !Array.isArray(inactive)) {
        return {
          labels: ['Active Users (0)', 'Inactive Users (0)'],
          datasets: [{
            data: [0, 0],
            backgroundColor: ['#3b82f6', '#f97316'], // Blue and orange matching backend
            borderWidth: 1
          }]
        };
      }

      const total = users.length;
      const inactive_count = inactive.length;
      const active_count = total - inactive_count;

      // Backend: generatePIIChart("pie", ["Active Users", "Inactive Users"], [values[1], values[2]], ["Blue", "orange"], ` `, 'right')
      // values[1] = active_count, values[2] = inactive_count
      return {
        labels: [
          `Active Users (${active_count})`,
          `Inactive Users (${inactive_count})`
        ],
        datasets: [{
          data: [active_count, inactive_count],
          backgroundColor: ['#3b82f6', '#f97316'], // Blue and orange matching backend
          borderWidth: 1
        }]
      };
    } catch (error) {
      console.log('Error MSUsersPieChartWidget', error);
      return {
        labels: ['Active Users (0)', 'Inactive Users (0)'],
        datasets: [{
          data: [0, 0],
          backgroundColor: ['#3b82f6', '#f97316'],
          borderWidth: 1
        }]
      };
    }
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "MS Users"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="ms365-users-pie-chart"
      data={data}
    />
  );
};

/**
 * MS MFA Pie Chart Widget
 * Displays a pie chart showing MFA Enabled vs MFA Disabled users
 * Based on template2/MS.js:237-251 (MFA pie chart)
 */
export const MSMFAPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend getUsersWithoutMFA logic
      // Backend: const users = data.MFA || [];
      const scanDataSource = data?.scanData || data?.data || data;
      const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};

      const mfaUsers = ms365.MFA || [];

      if (!Array.isArray(mfaUsers)) {
        return {
          labels: ['MFA Enabled (0)', 'MFA Disabled (0)'],
          datasets: [{
            data: [0, 0],
            backgroundColor: ['#3b82f6', '#f97316'], // Blue and orange matching backend
            borderWidth: 1
          }]
        };
      }

      // Backend: const Enabled = users.filter(user => user["MFA Enabled"] === true)
      // Backend: const Disabled = users.filter(user => user["MFA Enabled"] === false)
      const mfaEnabled = mfaUsers.filter(user => {
        const mfaStatus = user["MFA Enabled"] !== undefined
          ? user["MFA Enabled"]
          : user.mfaEnabled !== undefined
            ? user.mfaEnabled
            : user.MFAEnabled !== undefined
              ? user.MFAEnabled
              : false;
        return mfaStatus === true;
      });

      const mfaDisabled = mfaUsers.filter(user => {
        const mfaStatus = user["MFA Enabled"] !== undefined
          ? user["MFA Enabled"]
          : user.mfaEnabled !== undefined
            ? user.mfaEnabled
            : user.MFAEnabled !== undefined
              ? user.MFAEnabled
              : false;
        return mfaStatus === false;
      });

      const enabledCount = mfaEnabled.length;
      const disabledCount = mfaDisabled.length;

      // Backend: generatePIIChart("pie", ["MFA Enabled", "MFA Disabled"], [mfa_e.length, mfa_d.length], ["Blue", "orange"], ` `, 'right')
      return {
        labels: [
          `MFA Enabled (${enabledCount})`,
          `MFA Disabled (${disabledCount})`
        ],
        datasets: [{
          data: [enabledCount, disabledCount],
          backgroundColor: ['#3b82f6', '#f97316'], // Blue and orange matching backend
          borderWidth: 1
        }]
      };
    } catch (error) {
      console.log('Error MSMFAPieChartWidget', error);
      return {
        labels: ['MFA Enabled (0)', 'MFA Disabled (0)'],
        datasets: [{
          data: [0, 0],
          backgroundColor: ['#3b82f6', '#f97316'],
          borderWidth: 1
        }]
      };
    }
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "MS MFA"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="ms365-mfa-pie-chart"
      data={data}
    />
  );
};

/**
 * Inactive MS Users Summary (backward-compatible wrapper)
 * Now renders the combined InactiveMSUsersCostSummaryWidget
 */
export const InactiveMSUsersSummaryWidget = ({ data, title, config, ...props }) => {
  return <InactiveMSUsersCostSummaryWidget data={data} title={title || config?.title || "Inactive MS Users Cost Summary"} config={config} {...props} />;
};

/**
 * MS Policy Pie Chart Widget
 * Displays a pie chart showing Properly Configured vs Misconfigured/Disabled policies
 * Based on template2/MS.js:398-414 (Policy Configuration pie chart)
 */
export const MSPolicyPieChartWidget = ({ data, title, config }) => {
  const chartData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend PolicyConfigurationStatus logic
      const scanDataSource = data?.scanData || data?.data || data;
      const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};

      // Backend: const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"]
      const policyKeys = [
        "password_policies",
        "legacy_auth_enabled",
        "conditional_access_policies",
        "lack_of_dlp_policies",
        "misconfigured_exchange_forwarding"
      ];

      // Backend: const totalPolicies = policyKeys.length
      const totalPolicies = policyKeys.length;
      let disabledPoliciesCount = 0;

      // Backend logic: Check each policy
      for (const key of policyKeys) {
        let status;
        // Backend: if (key === "conditional_access_policies") { status = data[key] === 1 ? "Properly Configured" : "MisConfigured/Disabled"; } else { status = data[key] === 0 ? "Properly Configured" : "MisConfigured/Disabled"; }
        if (key === "conditional_access_policies") {
          status = ms365[key] === 1 ? "Properly Configured" : "No Data/Not Scanned";
        } else {
          status = ms365[key] === 0 ? "Properly Configured" : "No Data/Not Scanned";
        }

        // Backend: if (status === "MisConfigured/Disabled") disabledPoliciesCount++;
        if (status === "No Data/Not Scanned") {
          disabledPoliciesCount++;
        }
      }

      const properlyConfiguredCount = totalPolicies - disabledPoliciesCount;

      // Backend: generatePIIChart("pie", ["Properly Configured", "Misconfigured/Disabled"], [totalPolicies - disabledPoliciesCount, disabledPoliciesCount], ["Green", "Red"], ` `, 'right')
      return {
        labels: [
          `Properly Configured (${properlyConfiguredCount})`,
          `No Data/Not Scanned (${disabledPoliciesCount})`
        ],
        datasets: [{
          data: [properlyConfiguredCount, disabledPoliciesCount],
          backgroundColor: ['#22c55e', '#6b7280'], // Green and Gray matching user request
          borderWidth: 1
        }]
      };
    } catch (error) {
      console.log('Error MSPolicyPieChartWidget', error);
      return {
        labels: ['Properly Configured (0)', 'No Data/Not Scanned (0)'],
        datasets: [{
          data: [0, 0],
          backgroundColor: ['#22c55e', '#6b7280'],
          borderWidth: 1
        }]
      };
    }
  }, [data]);

  return (
    <PieChartWidget
      title={title || config?.title || "MS Policy"}
      chartData={chartData}
      height={250}
      config={config}
      widgetType="ms365-policy-pie-chart"
      data={data}
    />
  );
};

/**
 * MFA Summary (backward-compatible wrapper)
 * Now renders the combined MFACostSummaryWidget
 */
export const MFASummaryWidget = ({ data, title, config, ...props }) => {
  return <MFACostSummaryWidget data={data} title={title || config?.title || "MFA Cost Summary"} config={config} {...props} />;
};

/**
 * MFA Cost Summary (COMBINED with MFASummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const MFACostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    try {
      const scanDataSource = data?.scanData || data?.data || data;
      const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};

      const mfaUsers = ms365.MFA || [];
      const totalUsers = ms365.users?.length || 0;

      if (!Array.isArray(mfaUsers) || totalUsers === 0) {
        return { item: 'MFA', totalCount: 0, count: 0, unitCost: customUnitCost !== null ? customUnitCost : 50, itemCost: 0, riskPercent: '0%' };
      }

      const mfaDisabled = mfaUsers.filter(user => {
        const mfaStatus = user["MFA Enabled"] !== undefined ? user["MFA Enabled"]
          : user.mfaEnabled !== undefined ? user.mfaEnabled
          : user.MFAEnabled !== undefined ? user.MFAEnabled : false;
        return mfaStatus === false;
      });

      const noMfaCount = mfaDisabled.length;
      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['MFA'] || 50);
      const itemCost = unitCost * noMfaCount;
      const riskPercent = totalUsers > 0 ? `${Math.round(100 * noMfaCount / totalUsers)}%` : '0%';

      return {
        item: 'MFA',
        totalCount: totalUsers,
        count: noMfaCount,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error MFACostSummaryWidget', error);
      return { item: 'MFA', totalCount: 0, count: 0, unitCost: customUnitCost !== null ? customUnitCost : 50, itemCost: 0, riskPercent: '0%' };
    }
  }, [data, customUnitCost]);

  return (
    <SummaryTable
      title={title || config?.title || "MFA Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="mfa-cost-summary"
      data={data}
    />
  );
};

/**
 * Policy Configuration Cost Summary Widget
 * Displays cost breakdown for Policy Configuration remediation
 * Based on template2/MS.js:433-445 (Policy Configuration Cost Summary table)
 */
export const PolicyConfigurationCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend PolicyConfigurationStatus logic
      const scanDataSource = data?.scanData || data?.data || data;
      const ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || scanDataSource || {};

      // Backend: const policyKeys = ["password_policies", "legacy_auth_enabled", "conditional_access_policies", "lack_of_dlp_policies", "misconfigured_exchange_forwarding"]
      const policyKeys = [
        "password_policies",
        "legacy_auth_enabled",
        "conditional_access_policies",
        "lack_of_dlp_policies",
        "misconfigured_exchange_forwarding"
      ];

      let disabledPoliciesCount = 0;

      // Backend: for (const key of policyKeys) { ... if (status === "MisConfigured/Disabled") disabledPoliciesCount++; }
      for (const key of policyKeys) {
        let status;
        // Backend: if (key === "conditional_access_policies") { status = data[key] === 1 ? "Properly Configured" : "MisConfigured/Disabled"; } else { status = data[key] === 0 ? "Properly Configured" : "MisConfigured/Disabled"; }
        if (key === "conditional_access_policies") {
          status = ms365[key] === 1 ? "Properly Configured" : "No Data/Not Scanned";
        } else {
          status = ms365[key] === 0 ? "Properly Configured" : "No Data/Not Scanned";
        }

        // Backend: if (status === "MisConfigured/Disabled") disabledPoliciesCount++;
        if (status === "No Data/Not Scanned") {
          disabledPoliciesCount++;
        }
      }

      // Backend: const unitcost = unitCost("Policy Configuration") = 100 (from template2/Common.js:537)
      // Backend: const item_cost = unitcost * disabledPoliciesCount
      const unitCost = 100; // Backend unitCost("Policy Configuration") = 100
      const itemCost = unitCost * disabledPoliciesCount; // Backend: item_cost = unitcost * disabledPoliciesCount
      const totalCost = itemCost; // In backend, cost is accumulated, but for widget we show item_cost as total_cost

      return {
        item: 'Policy Configuration',
        unitCost: unitCost,
        itemCost: itemCost,
        totalCost: totalCost
      };
    } catch (error) {
      console.log('Error PolicyConfigurationCostSummaryWidget', error);
      return {
        item: 'Policy Configuration',
        unitCost: 100,
        itemCost: 0,
        totalCost: 0
      };
    }
  }, [data]);

  // Backend headers: ['Item', "Unit Cost", "Item Cost", "Total Cost"]
  // Backend rows: [["Policy Configuration", unitcost, item_cost, cost]]
  return (
    <SummaryTable
      title={title || config?.title || "Policy Configuration Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        5, // Total Count (5 policies checked)
        costData.itemCost / 100, // Count
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        `${Math.round(100 * (costData.itemCost / 100) / 5)}%`
      ]]}
      className="cost-summary"
      config={config}
      widgetType="policy-configuration-cost-summary"
      data={data}
    />
  );
};

/**
 * Inactive MS Users Cost Summary (COMBINED with InactiveMSUsersSummaryWidget)
 * Shows: Item, Total Count, Count, Unit Cost, Total Item Cost, Risk %
 */
export const InactiveMSUsersCostSummaryWidget = ({ data, title, config }) => {
  const costData = useMemo(() => {
    try {
      const scanDataSource = data?.scanData || data?.data || data;
      let ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};

      if (!ms365 || Object.keys(ms365).length === 0) {
        if (scanDataSource && (scanDataSource.users || scanDataSource.inactive_accounts)) {
          ms365 = scanDataSource;
        }
      }

      const users = ms365.users || ms365.Users || [];
      const inactive = ms365.inactive_accounts || ms365.inactiveAccounts || ms365['Inactive Accounts'] || ms365.inactive || [];

      if (!Array.isArray(inactive)) {
        return { item: 'MS Users', totalCount: 0, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
      }

      const totalUsers = Array.isArray(users) ? users.length : 0;
      const inactiveCount = inactive.length;
      const unitCost = 50;
      const itemCost = unitCost * inactiveCount;
      const riskPercent = totalUsers > 0 ? `${Math.round(100 * inactiveCount / totalUsers)}%` : '0%';

      return {
        item: 'MS Users',
        totalCount: totalUsers,
        count: inactiveCount,
        unitCost,
        itemCost,
        riskPercent
      };
    } catch (error) {
      console.log('Error InactiveMSUsersCostSummaryWidget', error);
      return { item: 'MS Users', totalCount: 0, count: 0, unitCost: 50, itemCost: 0, riskPercent: '0%' };
    }
  }, [data]);

  return (
    <SummaryTable
      title={title || config?.title || "Inactive MS Users Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalCount,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.riskPercent
      ]]}
      className="cost-summary"
      config={config}
      widgetType="inactive-ms-users-cost-summary"
      data={data}
    />
  );
};

/**
 * FSMO Cost Summary Widget
 * Displays cost breakdown for FSMO roles remediation
 * Based on template2/AD.js:683-695 (FSMO Cost Summary table)
 */
export const FSMOCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend _Ad_assess_fsmo_and_server_roles logic
      const scanDataSource = data?.scanData || data?.data || data;
      const adData = scanDataSource?.['Ad Scan'] || scanDataSource?.AD || scanDataSource || {};

      // Backend: const fsmo = AD.fsmo_roles || {};
      // fsmo_roles is an OBJECT, not an array
      const fsmo = adData?.fsmo_roles ||
        adData?.FSMO_roles ||
        adData?.fsmoRoles ||
        adData?.FSMORoles ||
        adData?.fsmo ||
        {};

      // Backend: const serverRoles = fsmo["Server Roles"] || [];
      const serverRoles = fsmo["Server Roles"] ||
        fsmo["server_roles"] ||
        fsmo["ServerRoles"] ||
        fsmo["serverRoles"] ||
        [];

      if (!fsmo || typeof fsmo !== 'object' || Object.keys(fsmo).length === 0) {
        return {
          item: 'FSMO',
          unitCost: 50,
          itemCost: 0,
          totalCost: 0
        };
      }

      // FSMO roles to check - match backend exactly
      const fsmoRolesList = [
        "Schema Master",
        "Domain Naming Master",
        "RID Master",
        "Infrastructure Master",
        "PDC Emulator"
      ];

      const report = [];

      // Check FSMO roles - match backend logic
      // Determine if all FSMO roles are on the same DC
      const holders = fsmoRolesList
        .filter(role => fsmo[role])
        .map(role => {
          const dn = fsmo[role];
          if (typeof dn === 'string') {
            return dn.split(",")[1] || "";
          }
          return "";
        })
        .filter(x => x);

      const uniqueHolders = [...new Set(holders)];
      const singleHolder = uniqueHolders.length === 1 ? uniqueHolders[0] : null;

      for (const role of fsmoRolesList) {
        if (!fsmo[role]) continue;
        const dn = fsmo[role];
        const dcNameMatch = typeof dn === 'string' ? dn.match(/CN=([^,]+)/g) : null;
        const dcName = dcNameMatch && dcNameMatch.length > 1 ? dcNameMatch[1].replace("CN=", "") : "Unknown";

        let risk = "Low";
        let notes = "";
        if (singleHolder) {
          risk = "High";
          notes = `All FSMO roles are on a single Domain Controller (${singleHolder}) — single point of failure.`;
        }
        report.push([
          role,
          dcName,
          risk,
          notes
        ]);
      }

      // Assess Server Roles - match backend logic
      for (const roleStr of serverRoles) {
        if (typeof roleStr !== 'string') continue;
        // roleStr format: "Role Name (ID: number)"
        const nameMatch = roleStr.match(/^(.+?)\s+\(ID:\s*(\d+)\)$/);
        if (!nameMatch) continue;

        const roleName = nameMatch[1];
        // const roleId = parseInt(nameMatch[2], 10);

        // Assign risk based on known risky roles on a DC
        let risk = "Low";
        let note = "Expected or low risk role";
        const highRiskRoles = ["DNS Server"];
        const mediumRiskRoles = [
          "File Server",
          "File and iSCSI Services",
          "File and Storage Services",
          "Remote Server Administration Tools",
          "Windows PowerShell ISE",
          "Windows PowerShell",
          "BitLocker Drive Encryption"
        ];

        if (highRiskRoles.includes(roleName)) {
          risk = "High";
          note = "Critical for AD operations; must be hardened and monitored.";
        } else if (mediumRiskRoles.includes(roleName)) {
          risk = "Medium";
          note = "Should be reviewed; potential attack surface or performance risk on DC.";
        }

        report.push([
          roleName,
          "-",
          risk,
          note
        ]);
      }

      // Filter for High/Medium risk issues - match backend: const issues = report.filter(issue => { if (issue[2] == 'High' || issue[2] == 'Medium') { return issue } })
      const issues = report.filter(issue => {
        const risk = issue[2]; // Risk is at index 2
        return risk === 'High' || risk === 'Medium';
      });

      // Match backend logic from Ad_FSMO_status:
      // count_fsmo_issue = values[1].length (issues array length)
      // fsmo_unit_cost = unitCost('FSMO') = 50
      // fsmo_cost = Math.round(fsmo_unit_cost * count_fsmo_issue)
      const countFSMOIssues = issues.length;
      // Use custom unit cost if provided, otherwise use default
      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['FSMO'] || 50);
      const itemCost = Math.round(unitCost * countFSMOIssues);
      const totalCost = itemCost; // In backend, cost is accumulated, but for widget we show item_cost as total_cost

      console.log('[FSMOCostSummaryWidget] FSMO assessment:', {
        totalRoles: report.length,
        issuesCount: countFSMOIssues,
        unitCost: unitCost,
        itemCost: itemCost
      });

      return {
        item: 'FSMO',
        unitCost: unitCost,
        itemCost: itemCost,
        totalCost: totalCost,
        count: countFSMOIssues,
        totalRoles: report.length
      };
    } catch (error) {
      console.log('Error FSMOCostSummaryWidget', error);
      console.error('Error FSMOCostSummaryWidget stack:', error.stack);
      return {
        item: 'FSMO',
        unitCost: customUnitCost !== null ? customUnitCost : (UNIT_COSTS['FSMO'] || 50),
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data, customUnitCost]);

  // Backend headers: ['Item', "Unit Cost", "Item Cost", "Total Cost"]
  // Backend rows: [["FSMO", fsmo_unit_cost, fsmo_cost, cost]]
  return (
    <SummaryTable
      title={title || config?.title || "FSMO Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalRoles,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.totalRoles > 0 ? `${Math.round(100 * costData.count / costData.totalRoles)}%` : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="fsmo-cost-summary"
      data={data}
    />
  );
};

/**
 * GPO Cost Summary Widget
 * Displays cost breakdown for GPO remediation
 * Based on template2/AD.js:745-757 (GPO Cost Summary table)
 */
export const GPOCostSummaryWidget = ({ data, customUnitCost = null, onUnitCostChange = null, title, config }) => {
  const costData = useMemo(() => {
    try {
      // Extract from raw scan data - match backend _Ad_assess_gpo_risks logic
      const scanDataSource = data?.scanData || data?.data || data;
      const adData = scanDataSource?.['Ad Scan'] || scanDataSource?.AD || scanDataSource || {};

      // Backend: const gpos = AD["GPO Detail"] || {};
      // GPO Detail is an OBJECT, not an array
      const gpos = adData?.["GPO Detail"] ||
        adData?.["GPO_Detail"] ||
        adData?.["gpo_detail"] ||
        adData?.["gpoDetail"] ||
        adData?.["GPODetail"] ||
        {};

      if (!gpos || typeof gpos !== 'object' || Array.isArray(gpos) || Object.keys(gpos).length === 0) {
        return {
          item: 'GPO',
          unitCost: 20,
          itemCost: 0,
          totalCost: 0
        };
      }

      // Match backend _Ad_assess_gpo_risks logic exactly
      const rows = [];
      const now = new Date();

      for (const [gpoName, gpo] of Object.entries(gpos)) {
        if (!gpo || typeof gpo !== 'object') continue;

        const createdOn = new Date(gpo.CreatedOn || gpo.createdOn || gpo.Created || Date.now());
        const lastModified = new Date(gpo.LastModified || gpo.lastModified || gpo.Modified || Date.now());
        const links = gpo.Links || gpo.links || [];
        const policies = gpo.Policies || gpo.policies || [];

        // Calculate how many days since last modification
        const daysSinceMod = Math.floor((now - lastModified) / (1000 * 60 * 60 * 24));

        // Risk flags - match backend logic
        let risk = "Low";
        let notes = [];

        // 1. Check if linked anywhere
        if (links.length === 0) {
          risk = "Medium";
          notes.push("GPO not linked to any scope");
        } else {
          // 2. Check if any link is disabled
          const disabledLinks = links.filter(l => {
            const enabled = l.Enabled !== undefined ? l.Enabled : (l.enabled !== undefined ? l.enabled : true);
            return !enabled;
          });
          if (disabledLinks.length > 0) {
            risk = risk === "Low" ? "Medium" : risk;
            notes.push("One or more GPO links are disabled");
          }
          // 3. Check for NoOverride links
          const noOverrideLinks = links.filter(l => {
            const noOverride = l.NoOverride !== undefined ? l.NoOverride : (l.noOverride !== undefined ? l.noOverride : false);
            return noOverride;
          });
          if (noOverrideLinks.length > 0) {
            risk = "Medium-High";
            notes.push("GPO has NoOverride set on one or more links");
          }
        }

        // 4. Check if last modified more than 365 days ago
        if (daysSinceMod > 365 && risk === "Low") {
          risk = "Low";
          notes.push(`Last modified ${daysSinceMod} days ago`);
        }

        // 5. Check for risky policy types (like printer deployments)
        const hasPrinterDeploy = policies.some(p => {
          const policyType = p.Type || p.type || '';
          return policyType === "Printer Deployment" || policyType === "Printer Deployment Policy";
        });
        if (hasPrinterDeploy && risk !== "Medium-High") {
          risk = "Medium";
          notes.push("Contains printer deployment policies");
        }

        // 6. Check if this is a default GPO (optional)
        const defaultGPOs = ["Default Domain Policy", "Default Domain Controllers Policy"];
        if (defaultGPOs.includes(gpoName)) {
          notes.push("Default system GPO");
        }

        rows.push([
          gpoName,
          createdOn.toISOString().split("T")[0],
          lastModified.toISOString().split("T")[0],
          links.length,
          policies.length,
          risk,
          notes.join("; ") || "-"
        ]);
      }

      // Filter for non-Low risk issues - match backend: const issues = rows.filter(issue => { if (issue[5] != 'Low') { return issue } })
      const issues = rows.filter(issue => {
        const risk = issue[5]; // Risk is at index 5
        return risk !== 'Low';
      });

      // Match backend logic from Ad_GPO_status:
      // count_gpo_issue = values[1].length (issues array length)
      // gpo_unit_cost = unitCost('GPO') = 20
      // gpo_cost = Math.round(gpo_unit_cost * count_gpo_issue)
      const countGPOIssues = issues.length;
      // Use custom unit cost if provided, otherwise use default
      const unitCost = customUnitCost !== null ? customUnitCost : (UNIT_COSTS['GPO'] || 20);
      const itemCost = Math.round(unitCost * countGPOIssues);
      const totalCost = itemCost; // In backend, cost is accumulated, but for widget we show item_cost as total_cost

      console.log('[GPOCostSummaryWidget] GPO assessment:', {
        totalGPOs: rows.length,
        issuesCount: countGPOIssues,
        unitCost: unitCost,
        itemCost: itemCost
      });

      return {
        item: 'GPO',
        unitCost: unitCost,
        itemCost: itemCost,
        totalCost: totalCost,
        count: countGPOIssues,
        totalGPOs: rows.length
      };
    } catch (error) {
      console.log('Error GPOCostSummaryWidget', error);
      console.error('Error GPOCostSummaryWidget stack:', error.stack);
      return {
        item: 'GPO',
        unitCost: customUnitCost !== null ? customUnitCost : (UNIT_COSTS['GPO'] || 20),
        itemCost: 0,
        totalCost: 0,
        count: 0
      };
    }
  }, [data, customUnitCost]);

  // Backend headers: ['Item', "Unit Cost", "Item Cost", "Total Cost"]
  // Backend rows: [["GPO", gpo_unit_cost, gpo_cost, cost]]
  return (
    <SummaryTable
      title={title || config?.title || "GPO Cost Summary"}
      headers={['Item', 'Total Count', 'Count', 'Unit Cost', 'Total Item Cost', 'Risk %']}
      rows={[[
        costData.item,
        costData.totalGPOs,
        costData.count,
        `$${costData.unitCost}`,
        `$${costData.itemCost}`,
        costData.totalGPOs > 0 ? `${Math.round(100 * costData.count / costData.totalGPOs)}%` : '0%'
      ]]}
      className="cost-summary"
      config={config}
      widgetType="gpo-cost-summary"
      data={data}
    />
  );
};

// ============================================================================
// TOTAL COST CALCULATOR WIDGET
// ============================================================================

/**
 * Helper function to get custom unit cost from all blocks
 * @param {string} itemName - Name of the item (e.g., 'Backups', 'Admin Accounts')
 * @param {Array} pages - Array of pages with blocks
 * @returns {number|null} - Custom unit cost or null if not found
 */
const getCustomUnitCostFromBlocks = (itemName, pages) => {
  if (!pages || !Array.isArray(pages)) return null;

  // Search through all blocks in all pages
  for (const page of pages) {
    if (!page.blocks || !Array.isArray(page.blocks)) continue;

    for (const block of page.blocks) {
      if (block.type === 'widget' && block.dataConfig?.customUnitCosts) {
        const customCost = block.dataConfig.customUnitCosts[itemName];
        if (customCost !== undefined && customCost !== null) {
          return customCost;
        }
      }
    }
  }

  return null;
};

/**
 * Total Cost Calculator
 * Sums all finding costs from all categories
 * Matches backend cost accumulation from New_template.js:1369-1420
 * Displays "Estimated Revenue Loss Due to Risks" as shown in final_page (line 669)
 * Now supports custom unit costs from all cost summary widgets
 */
export const TotalCostCalculatorWidget = ({ data, pages = null, title, config }) => {
  const totalCost = useMemo(() => {
    if (!data) return 0;

    const scanDataSource = data?.scanData || data?.data || data;
    let total = 0;

    // Helper to get unit cost (custom or default)
    const getUnitCost = (itemName, defaultCost) => {
      if (pages) {
        const customCost = getCustomUnitCostFromBlocks(itemName, pages);
        if (customCost !== null) return customCost;
      }
      return UNIT_COSTS[itemName] || defaultCost;
    };

    // Helper to check if a widget type is present in any page
    const hasWidget = (widgetType) => {
      if (!pages || !Array.isArray(pages)) return false;
      return pages.some(page => 
        page.blocks && Array.isArray(page.blocks) && 
        page.blocks.some(block => block.type === 'widget' && block.widgetType === widgetType)
      );
    };

    try {
      // ===== BACKUPS =====
      if (hasWidget('backups-cost-summary')) {
        let backupsCount = 0;
        const networkDevices = scanDataSource?.network_devices || [];
        for (const device of networkDevices) {
          const backups = device?.system?.['backups'];
          if (!backups) continue;
          const notes = backups['notes'] || backups['Notes'] || '';
          if (!notes.includes("Backup completed.")) {
            backupsCount++;
          }
        }
        total += getUnitCost('Backups', 50) * backupsCount;
      }

      // ===== ACCOUNTS =====
      if (hasWidget('accounts-cost-summary')) {
        let noexpire = 0;
        let regularAdminsCount = 0;
        const networkDevices = scanDataSource?.network_devices || [];
        networkDevices.forEach(device => {
          if (!device?.system || device.system === "WMI Error") return;
          const users = device?.system?.['Local Users'];
          if (Array.isArray(users)) {
            users.forEach(user => {
              const passwordExpires = user['Password Expires'] !== undefined
                ? user['Password Expires']
                : user['PasswordExpires'];
              if (!passwordExpires) {
                noexpire++;
              }
            });
          }
          const regularAdmins = device?.system?.['regular_admins'];
          if (Array.isArray(regularAdmins)) {
            regularAdminsCount += regularAdmins.length;
          }
        });
        total += getUnitCost('Accounts', 50) * (noexpire + regularAdminsCount);
      }

      // ===== ADMINS =====
      if (hasWidget('admins-cost-summary')) {
        let noexpire = 0;
        let regularAdminsCount = 0;
        const networkDevices = scanDataSource?.network_devices || [];
        networkDevices.forEach(device => {
          if (!device?.system || device.system === "WMI Error") return;
          const users = device?.system?.['Administrators'];
          if (Array.isArray(users)) {
            users.forEach(user => {
              const passwordExpires = user['Password Expires'] !== undefined
                ? user['Password Expires']
                : user['PasswordExpires'];
              if (!passwordExpires) {
                noexpire++;
              }
            });
          }
          const regularAdmins = device?.system?.['regular_admins'];
          if (Array.isArray(regularAdmins)) {
            regularAdminsCount += regularAdmins.length;
          }
        });
        total += getUnitCost('Admin Accounts', 100) * (noexpire + regularAdminsCount);
      }

      // ===== FIREWALL (NETWORK) =====
      if (hasWidget('network-firewall-cost-summary')) {
        const networkDevices = scanDataSource?.network_devices || [];
        const validDevices = networkDevices.filter(device => device.system && device.system !== "WMI Error");
        let notFullyEnabledCount = 0;
        for (const device of validDevices) {
          const firewall = device?.system?.Firewall;
          if (!firewall || typeof firewall !== 'object') {
            notFullyEnabledCount++;
            continue;
          }
          const enabledCount = [firewall.Domain === true, firewall.Private === true, firewall.Public === true].filter(Boolean).length;
          if (enabledCount < 3) notFullyEnabledCount++;
        }
        total += (UNIT_COSTS['Firewall'] || 50) * notFullyEnabledCount;
      }

      // ===== FIREWALL (LOCAL/LEGACY) =====
      if (hasWidget('firewall-cost-summary')) {
        const firewallDevices = extractFirewallData(data);
        let firewallDisabled = 0;
        firewallDevices.forEach(device => {
          const status = device.status.toLowerCase();
          if (status.includes('disabled') || status.includes('off') || status.includes('not running')) {
            firewallDisabled++;
          }
        });
        total += (UNIT_COSTS['Firewall'] || 50) * firewallDisabled;
      }

      // ===== LOCALHOST FIREWALL =====
      if (hasWidget('localhost-firewall-cost-summary')) {
        const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
        let notFullyEnabledCount = 0;
        const firewall = localhost?.["System"]?.Firewall || localhost?.Firewall;
        if (!firewall || typeof firewall !== 'object') {
          notFullyEnabledCount++;
        } else {
          const domain = firewall.Domain === true;
          const privateNet = firewall.Private === true;
          const publicNet = firewall.Public === true;
          const enabledCount = [domain, privateNet, publicNet].filter(Boolean).length;
          if (enabledCount < 3) notFullyEnabledCount++;
        }
        total += 50 * notFullyEnabledCount;
      }

      // ===== OS =====
      if (hasWidget('os-cost-summary')) {
        const networkDevices = scanDataSource?.network_devices || [];
        let osIssues = 0;
        networkDevices.forEach(device => {
          if (!device.system || device.system === "WMI Error") return;
          const OS = device.system.OS;
          if (!OS) return;
          if (/Windows XP|Vista|Windows 7|Windows 8(?!\.1)|Server 2003|Server 2008|Server 2012/.test(OS)) {
            osIssues++;
          } else if (/Windows 10|Server 2016|Server 2019/.test(OS)) {
            osIssues++;
          }
        });
        total += getUnitCost('OS', 50) * osIssues;
      }

      // ===== LOCALHOST OS =====
      if (hasWidget('localhost-os-cost-summary')) {
        const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
        let critical = 0;
        let moderate = 0;
        const hardware = localhost?.["Hardware Informations"] || {};
        const osName = hardware?.["OS"] || hardware?.OS || "";
        const osVersion = hardware?.["OS Version"] || hardware?.["OS_Version"] || hardware?.OSVersion || "";
        const fullOS = `${osName} ${osVersion}`.trim();
        if (fullOS) {
          if (/Windows XP|Vista|7|8(?!\.1)|2003|2008|2012/i.test(fullOS)) {
            critical++;
          } else if (/Windows 10|2016|2019/i.test(fullOS)) {
            moderate++;
          }
        }
        total += 50 * (critical + moderate);
      }

      // ===== SERVICES =====
      if (hasWidget('network-service-cost-summary')) {
        const networkDevices = scanDataSource?.network_devices || [];
        const validDevices = networkDevices.filter(device =>
          device?.system && device.system !== "WMI Error"
        );
        const result = analyzeNetworkServices(validDevices);
        if (result) {
          const [, suspicious] = result;
          total += getUnitCost('Services', 50) * suspicious.length;
        }
      }

      // ===== LOCALHOST SERVICES =====
      if (hasWidget('localhost-service-cost-summary')) {
        const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
        const suspicious = [];
        const services = localhost?.['Hardware Informations']?.['Non-Default Services'];
        if (Array.isArray(services)) {
          for (const service of services) {
            const account = service['Start Account'];
            const state = service['State'];
            const startMode = service['Start Mode'];
            if (["localSystem", "Administrator"].includes(account)) {
              if (state === 'Running' && startMode === 'Auto') {
                suspicious.push(service);
              }
            }
          }
          total += 50 * suspicious.length;
        }
      }

      // ===== OPEN PORTS =====
      if (hasWidget('network-open-ports-cost-summary')) {
        const networkDevices = scanDataSource?.network_devices || [];
        const sensitive_ports = {
          21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP",
          53: "DNS", 80: "HTTP", 139: "NetBIOS", 143: "SMB",
          3306: "MySQL", 3389: "RDP", 8080: "HTTP"
        };
        const rows = [];
        for (const device of networkDevices) {
          const ip = device['ip'] || device['hostname'] || '';
          const open_ports = device['open_ports'] || {};
          if (open_ports && typeof open_ports === 'object' && !Array.isArray(open_ports)) {
            Object.keys(open_ports).forEach(key => {
              if (sensitive_ports[key]) {
                rows.push([ip, key, sensitive_ports[key]]);
              }
            });
          }
        }
        const sensitive_port_count = new Set(rows.map(entry => entry[0]));
        total += getUnitCost('Open Ports', 30) * sensitive_port_count.size;
      }

      // ===== LOCALHOST OPEN PORTS =====
      if (hasWidget('localhost-open-ports-cost-summary')) {
        const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || scanDataSource || {};
        const sensitive_ports = {
          21: "FTP", 22: "SSH", 23: "Telnet", 25: "SMTP",
          53: "DNS", 80: "HTTP", 139: "NetBIOS", 143: "SMB",
          3306: "MySQL", 3389: "RDP", 8080: "HTTP"
        };
        const openPorts = localhost?.["External vulnerability"]?.["open_ports"] ||
          localhost?.["External Vulnerability"]?.["open_ports"] ||
          localhost?.["external_vulnerability"]?.["open_ports"] ||
          {};
        if (openPorts && typeof openPorts === 'object') {
          const rows = [];
          const ipList = Object.keys(openPorts);
          for (const ip of ipList) {
            const ports = openPorts[ip];
            if (!ports || typeof ports !== 'object') continue;
            for (const portKey of Object.keys(ports)) {
              const portNum = parseInt(portKey);
              if (sensitive_ports[portNum]) {
                rows.push([ip, portNum, sensitive_ports[portNum]]);
              }
            }
          }
          const hasSensitive = rows.length > 0 ? 1 : 0;
          total += 30 * hasSensitive;
        }
      }

      // ===== ENCRYPTION =====
      if (hasWidget('network-encryption-cost-summary')) {
        const networkDevices = scanDataSource?.network_devices || [];
        const localhost = scanDataSource?.localhost || scanDataSource?.['Localhost Scan'] || null;
        let unencryptedCount = 0;
        const checkIsEncrypted = (encryption) => {
          if (encryption === undefined || encryption === null) return null;
          if (typeof encryption === 'string') {
            const encLower = encryption.toLowerCase();
            if (!encLower.includes('disabled') && !encLower.includes('off') && !encLower.includes('not enabled')) {
              return true;
            }
            return false;
          } else if (Array.isArray(encryption)) {
            if (encryption.length === 0) return false;
            for (const enc of encryption) {
              const status = (Array.isArray(enc) ? enc[1] : enc) || '';
              if (status.toLowerCase().includes('(encrypted)')) {
                return true;
              }
            }
            return false;
          } else if (typeof encryption === 'object') {
            if (encryption.enabled === true || encryption.status === 'Enabled' || encryption.Enabled === true) {
              return true;
            }
            return false;
          }
          return false;
        };
        const processDevice = (device) => {
          if (!device || !device.system || device.system === "WMI Error") return;
          const encryption = device.system.Encryption || device.system.encryption;
          if (encryption === undefined) return;
          const isEncrypted = checkIsEncrypted(encryption);
          if (isEncrypted === false) {
            unencryptedCount++;
          }
        };
        networkDevices.forEach(processDevice);
        if (localhost) {
          const encryption = localhost?.System?.Encryption || 
                             localhost?.["Hardware Informations"]?.Encryption || 
                             localhost?.Encryption || 
                             localhost?.encryption ||
                             localhost?.System?.encryption ||
                             localhost?.["Hardware Informations"]?.encryption;
          if (encryption !== undefined) {
            const isEncrypted = checkIsEncrypted(encryption);
            if (isEncrypted === false) {
              unencryptedCount++;
            }
          }
        }
        total += getUnitCost('Encryption', 50) * unencryptedCount;
      }

      // ===== PII =====
      if (hasWidget('pii-cost-summary')) {
        const combinedPii = scanDataSource?.PII || scanDataSource?.['PII'] || scanDataSource?.pii || scanDataSource?.combinedPii || scanDataSource?.['PII Detection'] || {};
        const totalPII = Object.values(combinedPii).reduce((sum, entries) => {
          return sum + (Array.isArray(entries) ? entries.length : 0);
        }, 0);
        total += getUnitCost('PII', 100) * totalPII;
      }

      // ===== MS USERS =====
      if (hasWidget('inactive-ms-users-cost-summary')) {
        let ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
        if (!ms365 || Object.keys(ms365).length === 0) {
          if (scanDataSource && (scanDataSource.users || scanDataSource.inactive_accounts)) {
            ms365 = scanDataSource;
          }
        }
        const inactive = ms365.inactive_accounts || ms365.inactiveAccounts || ms365['Inactive Accounts'] || ms365.inactive || [];
        if (Array.isArray(inactive)) {
          total += 50 * inactive.length;
        }
      }

      // ===== MFA =====
      if (hasWidget('mfa-cost-summary')) {
        let ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
        const mfaUsers = ms365.MFA || [];
        const totalUsers = ms365.users?.length || 0;
        if (Array.isArray(mfaUsers) && totalUsers > 0) {
          const mfaDisabled = mfaUsers.filter(user => {
            const mfaStatus = user["MFA Enabled"] !== undefined ? user["MFA Enabled"]
              : user.mfaEnabled !== undefined ? user.mfaEnabled
              : user.MFAEnabled !== undefined ? user.MFAEnabled : false;
            return mfaStatus === false;
          });
          total += getUnitCost('MFA', 50) * mfaDisabled.length;
        }
      }

      // ===== POLICY CONFIGURATION =====
      if (hasWidget('policy-configuration-cost-summary')) {
        let ms365 = scanDataSource?.MS365 || scanDataSource?.['Microsoft 365 Scan'] || {};
        const policyKeys = [
          "password_policies",
          "legacy_auth_enabled",
          "conditional_access_policies",
          "lack_of_dlp_policies",
          "misconfigured_exchange_forwarding"
        ];
        let disabledPoliciesCount = 0;
        for (const key of policyKeys) {
          let status;
          if (key === "conditional_access_policies") {
            status = ms365[key] === 1 ? "Properly Configured" : "No Data/Not Scanned";
          } else {
            status = ms365[key] === 0 ? "Properly Configured" : "No Data/Not Scanned";
          }
          if (status === "No Data/Not Scanned") {
            disabledPoliciesCount++;
          }
        }
        total += 100 * disabledPoliciesCount;
      }

      // ===== AD COMPUTERS =====
      if (hasWidget('ad-computer-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const inactives = adData?.['inactive_computers'] || [];
        const inactiveComputersCount = Array.isArray(inactives) ? inactives.length : 0;
        total += 50 * inactiveComputersCount;
      }

      // ===== AD USERS =====
      if (hasWidget('ad-users-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const inactives = adData?.['inactive_accounts'] || [];
        const inactiveUsersCount = Array.isArray(inactives) ? inactives.length : 0;
        total += 50 * inactiveUsersCount;
      }

      // ===== AD GROUPS =====
      if (hasWidget('ad-groups-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const groups = adData?.groups || [];
        if (Array.isArray(groups)) {
          const privilegedGroups = [
            "Domain Admins", "Enterprise Admins", "Administrators", "Schema Admins",
            "Account Operators", "Backup Operators", "Remote Desktop Users",
            "Network Configuration Operators", "Power Users"
          ];
          let countGroupIssue = 0;
          for (const group of groups) {
            const groupName = group.name || group.Name || group.GroupName || '';
            const groupMembers = Array.isArray(group.members) ? group.members : (group.Members || []);
            const memberCount = Array.isArray(groupMembers) ? groupMembers.length : 0;
            const groupIsSecurityGroup = group.isSecurityGroup !== undefined ? group.isSecurityGroup : (group.IsSecurityGroup || group.GroupType === 2 || group.Type === 'Security');
            const groupIsDomainLocal = group.isDomainLocal !== undefined ? group.isDomainLocal : (group.IsDomainLocal || group.GroupScope === 2 || group.Scope === 'Domain Local');
            const groupManagedBy = group.managedBy || group.ManagedBy || null;

            const isPrivileged = privilegedGroups.includes(groupName);
            const hasManager = !!groupManagedBy;

            let risk = "Low";
            if (isPrivileged && memberCount > 0) {
              risk = "High";
            } else if (isPrivileged && memberCount === 0) {
              risk = "Medium";
            } else if (!hasManager && groupIsSecurityGroup) {
              risk = "Medium";
            } else if (groupIsDomainLocal && groupIsSecurityGroup) {
              risk = "Medium";
            }
            if (risk !== "Low") {
              countGroupIssue++;
            }
          }
          total += 40 * countGroupIssue;
        }
      }

      // ===== AD OUS =====
      if (hasWidget('ou-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const ous = adData?.['OU_list'] || [];
        if (Array.isArray(ous)) {
          let countOUIssue = 0;
          for (const ou of ous) {
            const name = ou.ou || ou.OU || "Unnamed OU";
            const members = ou.members || ou.Members || [];
            const memberCount = Array.isArray(members) ? members.length : 0;
            let risk = "Low";
            const lowerName = name.toLowerCase();
            if (lowerName.includes("disabled") && memberCount > 0) {
              risk = "High";
            } else if (/admin|backup|service/.test(lowerName)) {
              risk = "Medium";
            } else if (memberCount === 0) {
              risk = "Medium";
            }
            if (risk !== "Low") {
              countOUIssue++;
            }
          }
          total += 40 * countOUIssue;
        }
      }

      // ===== FSMO ROLES =====
      if (hasWidget('fsmo-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const serverRoles = adData?.['Server Roles'] || adData?.server_roles || [];
        if (Array.isArray(serverRoles)) {
          const report = [];
          for (const roleStr of serverRoles) {
            if (typeof roleStr !== 'string') continue;
            const nameMatch = roleStr.match(/^(.+?)\s+\(ID:\s*(\d+)\)$/);
            if (!nameMatch) continue;
            const roleName = nameMatch[1];
            let risk = "Low";
            const highRiskRoles = ["DNS Server"];
            const mediumRiskRoles = [
              "File Server", "File and iSCSI Services", "File and Storage Services",
              "Remote Server Administration Tools", "Windows PowerShell ISE",
              "Windows PowerShell", "BitLocker Drive Encryption"
            ];
            if (highRiskRoles.includes(roleName)) {
              risk = "High";
            } else if (mediumRiskRoles.includes(roleName)) {
              risk = "Medium";
            }
            report.push([roleName, "-", risk, ""]);
          }
          const issues = report.filter(issue => issue[2] === 'High' || issue[2] === 'Medium');
          const countFSMOIssues = issues.length;
          const unitCost = getUnitCost('FSMO', 50);
          total += Math.round(unitCost * countFSMOIssues);
        }
      }

      // ===== GPO =====
      if (hasWidget('gpo-cost-summary')) {
        const adData = scanDataSource?.['Ad Scan'] || {};
        const gpos = adData?.["GPO Detail"] || adData?.["GPO_Detail"] || adData?.["gpo_detail"] || adData?.["gpoDetail"] || adData?.["GPODetail"] || {};
        if (gpos && typeof gpos === 'object' && !Array.isArray(gpos)) {
          const rows = [];
          const now = new Date();
          for (const [gpoName, gpo] of Object.entries(gpos)) {
            if (!gpo || typeof gpo !== 'object') continue;
            const lastModified = new Date(gpo.LastModified || gpo.lastModified || gpo.Modified || Date.now());
            const links = gpo.Links || gpo.links || [];
            const policies = gpo.Policies || gpo.policies || [];
            const daysSinceMod = Math.floor((now - lastModified) / (1000 * 60 * 60 * 24));
            let risk = "Low";
            if (links.length === 0) {
              risk = "Medium";
            } else {
              const disabledLinks = links.filter(l => {
                const enabled = l.Enabled !== undefined ? l.Enabled : (l.enabled !== undefined ? l.enabled : true);
                return !enabled;
              });
              if (disabledLinks.length > 0) {
                risk = risk === "Low" ? "Medium" : risk;
              }
              const noOverrideLinks = links.filter(l => {
                const noOverride = l.NoOverride !== undefined ? l.NoOverride : (l.noOverride !== undefined ? l.noOverride : false);
                return noOverride;
              });
              if (noOverrideLinks.length > 0) {
                risk = "Medium-High";
              }
            }
            if (daysSinceMod > 365 && risk === "Low") {
              risk = "Low";
            }
            const hasPrinterDeploy = policies.some(p => {
              const policyType = p.Type || p.type || '';
              return policyType === "Printer Deployment" || policyType === "Printer Deployment Policy";
            });
            if (hasPrinterDeploy && risk !== "Medium-High") {
              risk = "Medium";
            }
            rows.push([gpoName, null, null, null, null, risk]);
          }
          const issues = rows.filter(issue => issue[5] !== 'Low');
          const unitCost = getUnitCost('GPO', 20);
          total += Math.round(unitCost * issues.length);
        }
      }

      // ===== AD PRIVILEGED ADMINS =====
      if (hasWidget('ad-admins-cost-summary')) {
        const allAdmins = extractADAdminsData(data);
        const enabledAdmins = allAdmins.filter(a => a.isEnabled);
        const unitCost = getUnitCost('Admin Accounts', 100);
        total += Math.ceil(unitCost * enabledAdmins.length);
      }

    } catch (error) {
      console.log('Error TotalCostCalculatorWidget', error);
      console.error('Error TotalCostCalculatorWidget stack:', error.stack);
    }

    return Math.round(total);
  }, [data, pages]);

  // Get label styles
  const effectiveWidgetType = config.widgetType || 'total-cost-calculator';
  const titleStyle = getLabelStyle(config, 'title', {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center'
  }, effectiveWidgetType, data);
  const amountStyle = getLabelStyle(config, 'amount', {
    fontSize: '36px',
    fontWeight: '800',
    color: '#dc2626',
    textAlign: 'center'
  }, effectiveWidgetType, data);
  const noteStyle = getLabelStyle(config, 'note', {
    fontSize: '14px',
    fontWeight: '400',
    color: '#6b7280',
    textAlign: 'center'
  }, effectiveWidgetType, data);


  // Layout handling
  const layout = config.layout || 'column';
  const horizontalAlign = config.horizontalAlign || 'center';
  const verticalAlign = config.verticalAlign || 'center';
  
  // Map to flexbox properties based on row/column layout
  const flexJustifyContent = layout === 'row' ? horizontalAlign : verticalAlign;
  let flexAlignItems = layout === 'row' ? verticalAlign : horizontalAlign;

  if (['space-between', 'space-around', 'space-evenly'].includes(flexAlignItems)) {
    flexAlignItems = 'center';
  }

  const textTitle = title || config?.title || "Estimated Revenue Loss Due to Risks";
  const textNote = config?.noteText !== undefined ? config.noteText : "This is the sum of all finding costs across all security categories.";

  // Content Toggles
  const showTitle = config.showTitle !== false;
  const showAmount = config.showAmount !== false;
  const showNote = config.showNote !== false;

  const getVerticalJustify = (align) => {
    if (align === 'top') return 'flex-start';
    if (align === 'bottom') return 'flex-end';
    return 'center';
  };

  const { verticalAlign: titleVAlign, ...titleStyleProps } = titleStyle;
  const { verticalAlign: amountVAlign, ...amountStyleProps } = amountStyle;
  const { verticalAlign: noteVAlign, ...noteStyleProps } = noteStyle;

  return (
    <div 
      className="total-cost-calculator" 
      style={{
        ...config.style,
        display: 'flex',
        flexDirection: layout,
        alignItems: flexAlignItems,
        justifyContent: flexJustifyContent,
        gap: layout === 'row' ? '24px' : '0'
      }}
    >
      {showTitle && <div className="total-cost-header" style={{ ...titleStyleProps, display: 'flex', flexDirection: 'column', justifyContent: getVerticalJustify(titleVAlign), marginBottom: layout === 'row' ? '0' : (showAmount || showNote ? '16px' : '0'), flex: layout === 'row' ? '1' : 'auto', width: layout === 'column' ? '100%' : 'auto' }}>{textTitle}</div>}
      {showAmount && <div className="total-cost-amount" style={{ ...amountStyleProps, display: 'flex', flexDirection: 'column', justifyContent: getVerticalJustify(amountVAlign), margin: layout === 'row' ? '0' : ((showTitle ? '16px 0' : '0 0 16px 0') && (showNote ? '16px 0' : '16px 0 0 0')), flex: layout === 'row' ? '1' : 'auto', width: layout === 'column' ? '100%' : 'auto' }}>${totalCost.toLocaleString()}</div>}
      {showNote && <div className="total-cost-note" style={{ ...noteStyleProps, display: 'flex', flexDirection: 'column', justifyContent: getVerticalJustify(noteVAlign), marginTop: layout === 'row' ? '0' : (showTitle || showAmount ? '12px' : '0'), flex: layout === 'row' ? '1' : 'auto', width: layout === 'column' ? '100%' : 'auto' }}>
        {textNote}
      </div>}
    </div>
  );
};

// Export all widgets
const SummaryFindingsWidgets = {
  // Backups
  BackupsSummaryWidget,
  BackupsCostSummaryWidget,

  // Admins
  AdminsPasswordStatusPieChartWidget,
  AdminsFindingSummaryWidget,
  AdminsCostSummaryWidget,

  // Accounts
  AccountsPasswordStatusPieChartWidget,
  AccountsFindingSummaryWidget,
  AccountsCostSummaryWidget,

  // Firewall
  FirewallFindingSummaryWidget,
  FirewallCostSummaryWidget,
  LocalhostFirewallFindingsSummaryWidget,
  LocalhostFirewallCostSummaryWidget,
  LocalhostServiceRiskSummaryWidget,
  LocalhostServiceCostSummaryWidget,
  LocalhostOSCostSummaryWidget,
  LocalhostOpenPortsCostSummaryWidget,

  // OS
  OSRiskSummaryWidget,
  OSCostSummaryWidget,

  // Services Summary
  NetworkServiceRiskSummaryWidget,
  NetworkServiceCostSummaryWidget,

  // Open Ports Summary
  NetworkOpenPortsCostSummaryWidget,

  // AD Computer Cost Summary
  ADComputerCostSummaryWidget,

  // AD Users
  ADUsersCostSummaryWidget,

  // OU Cost Summary
  OUCostSummaryWidget,

  // AD Groups Cost Summary
  ADGroupsCostSummaryWidget,

  // FSMO Cost Summary
  FSMOCostSummaryWidget,

  // GPO Cost Summary
  GPOCostSummaryWidget,

  // MS365 Users
  MSUsersPieChartWidget,
  MSMFAPieChartWidget,
  MSPolicyPieChartWidget,
  InactiveMSUsersSummaryWidget,
  InactiveMSUsersCostSummaryWidget,
  MFASummaryWidget,
  MFACostSummaryWidget,

  // PII
  PIIDetectionPieChartWidget,
  PIIFindingSummaryWidget,
  PIICostSummaryWidget,

  // Total Cost
  TotalCostCalculatorWidget
};

export default SummaryFindingsWidgets;
